/* ------------------------------------------------------------------------
 * Vertical / state inference and the grounding gate
 *
 * Lifted VERBATIM from the single-file build — not rewritten, not tidied.
 * The detector behaviour in here is the product, and it carries a long tail
 * of corrections derived from real policy paper: a London Market Reform
 * Contract stating cover as sums insured; triggers stated in a declarations
 * table; the rule that a limit is a multiple of 500 and at least $1,000, so a
 * policy number cannot be printed as a limit; a declarations schedule vetoing
 * its own policy wording. 1,053 assertions cover that behaviour and they only
 * keep covering it while the code stays byte-identical.
 *
 * Cross-module calls still go through the `window.*` registry the original
 * used. That is deliberate: rewiring ~190 functions into an import graph is a
 * separate change with its own risk, and this one moves the code and nothing
 * else. See MIGRATION.md.
 *
 * From index.html, script block 5 (894 lines).
 * ---------------------------------------------------------------------- */


/*
 * Replaces the removed "Industry Vertical" and "State(s)" user inputs.
 * Verticals and states are inferred ONLY from the uploaded policy document
 * text and the organization/website overview. Every claim is passed through
 * spVerifyGrounding() — a finding is emitted ONLY when its trigger term is
 * actually present in one of the source texts, and the matched snippet +
 * source are attached as evidence. Nothing is asserted that isn't quoted
 * from a real source, so the output cannot hallucinate a vertical/state.
 */
(function(){
  // vertical -> trigger terms (lowercased substrings, matched case-insensitively).
  // A vertical is claimed only if >=1 of its terms appears verbatim in a source.
  const SP_VERTICAL_KEYWORDS = {
    'Public School District': ['school district','independent school district',' isd ','board of trustees','public school'],
    // Charter language, as it actually appears on Texas paper. One term was too
    // few: a charter school's own policy says "charter holder" and
    // "open-enrollment", and with a single trigger it scored below Higher
    // Education on the same document. See SP_VERTICAL_ANTI below.
    'Charter School': ['charter school','charter holder','open-enrollment charter','open enrollment charter','public charter','charter district'],
    'Private School': ['private school','parochial school','preparatory school'],
    // 'campus' and bare 'college' are gone. Both are ordinary K-12 words:
    // Texas calls every school site a campus, and a classical K-8 academy's copy
    // is full of "college preparatory" and "college readiness". Arlington
    // Classics Academy, a K-8 charter, scored two Higher Education hits
    // ('college', 'campus') against one for Charter School and was reported as
    // higher education. What is left cannot be said about a school district.
    'Higher Education': ['university','higher education','community college','junior college','undergraduate','graduate program','residence hall','dormitory','provost'],
    'Early Childhood/Daycare': ['daycare','child care','childcare','early childhood','preschool','pre-school'],
    'Tutoring & Test Prep Services': ['tutoring','test prep','test preparation'],
    'Hospital/Health System': ['hospital','health system','medical center'],
    'Medical Practice': ['medical practice','physician','clinic','outpatient'],
    'Dental Practice': ['dental','dentist','orthodont'],
    'Behavioral Health': ['behavioral health','mental health','psychiatric','substance abuse'],
    'Long-Term Care/Nursing': ['nursing home','long-term care','long term care','skilled nursing','assisted living'],
    'Home Health': ['home health','home care','hospice'],
    'Oil & Gas Exploration': ['oil and gas','oil & gas','exploration and production','upstream','drilling','wellsite','well site'],
    'Oil & Gas Services': ['oilfield','oil field','oilfield services','fracking','hydraulic fracturing','completion services'],
    // Generation-side renewables: the party that owns/builds the generating
    // asset. NOTE: bare 'battery' is deliberately NOT a trigger here or below —
    // GL forms carry an "assault and battery" exclusion, so it would fire on
    // almost every liability policy in the book.
    'Renewable Energy': ['solar','solar panel','solar farm','solar installation','wind farm','wind turbine',
      'renewable energy','photovoltaic','clean energy','geothermal','biomass','hydroelectric',
      'utility-scale','power purchase agreement','interconnection agreement','net metering','megawatt'],
    // Storage / distributed energy is a different risk from a solar farm: the
    // exposure is installed product at customer premises, high-voltage
    // electrical work in occupied homes, lithium-ion fire/thermal runaway, and
    // — where the insured also sells the power — utility regulatory exposure.
    // Base Power (home batteries + Texas retail electricity) is this vertical,
    // not 'Renewable Energy' and not 'Utilities'.
    'Battery Storage & Distributed Energy': ['battery storage','energy storage','battery energy storage',
      'home battery','battery system','battery installation','batteries installed','storage battery',
      'lithium-ion','lithium ion','thermal runaway','virtual power plant','distributed energy',
      'distributed generation','microgrid','grid-scale','behind-the-meter','demand response',
      'backup power','standby power','ev charging','electric vehicle charging','kilowatt-hour','kwh'],
    // A retail electric provider resells power it does not generate. Texas is
    // the deregulated market where this is common, and it drags in PUC
    // licensing, bonding and E&O exposure that neither generation nor storage
    // implies on its own.
    'Retail Electricity Provider': ['retail electricity','retail electric provider','electricity provider',
      'electricity plan','energy plan','deregulated energy','power to choose','electricity supplier',
      'retail energy provider','public utility commission'],
    'Mining': ['mining','mine site','quarry','mineral extraction'],
    'Utilities': ['utility','electric utility','power generation','transmission and distribution'],
    'Construction': ['construction','contractor','general contractor','subcontractor','jobsite','job site','builders risk'],
    'Construction — Heavy/Highway': ['heavy highway','highway construction','road construction','bridge construction'],
    'Commercial Real Estate': ['commercial real estate','office building','retail center','commercial property'],
    'Property Management': ['property management','property manager','landlord','tenant'],
    'Residential Development': ['residential development','homebuilder','home builder','residential construction'],
    'SaaS/Software': ['saas','software as a service','software platform','cloud software'],
    'IT Services/MSP': ['managed service provider',' msp ','it services','managed it'],
    'Cybersecurity': ['cybersecurity','cyber security','penetration testing','soc 2'],
    'Telecommunications': ['telecommunications','telecom','wireless carrier','broadband'],
    'Trucking/Fleet': ['trucking','motor carrier','fleet of','commercial auto','tractor trailer','dot number'],
    'Aviation': ['aviation','aircraft','airport','airline','hangar'],
    'Maritime': ['maritime','marine','vessel','shipyard','longshore','jones act','wharfinger'],
    'Warehousing/Distribution': ['warehouse','warehousing','distribution center','fulfillment center','3pl'],
    'Food/Beverage Manufacturing': ['food manufacturing','beverage manufacturing','food processing','bottling'],
    'Pharmaceutical Manufacturing': ['pharmaceutical','drug manufacturing','biopharmaceutical'],
    'Chemical Manufacturing': ['chemical manufacturing','chemical plant','petrochemical'],
    'General Manufacturing': ['manufacturing','manufacturer','fabrication','assembly plant','production facility'],
    'Automotive Manufacturing': ['automotive manufacturing','auto parts','vehicle assembly'],
    'Restaurants/Bars': ['restaurant','bar and grill','food service','hospitality','liquor liability'],
    'Hotels/Resorts': ['hotel','resort','lodging','motel'],
    'Law Firm': ['law firm','attorney','legal services','lawyers professional'],
    'Accounting Firm': ['accounting firm','cpa','certified public accountant'],
    'Architecture/Engineering': ['architecture','engineering firm','architects and engineers',' a/e ','design professional'],
    'Consulting': ['consulting','consultant','advisory services'],
    // 'human resources' is gone: every organisation has an HR department, and
    // a nav link reading "Human Resources and Student Services" made Arlington
    // Classics Academy score as a staffing business. What is left describes a
    // firm that SELLS staffing, which a school does not.
    'Staffing/HR': ['staffing agency','staffing services','temporary staffing','temp agency',
      'professional employer','peo','employer of record','hr outsourcing','recruiting firm'],
    '501(c)(3) Nonprofit': ['nonprofit','non-profit','501(c)(3)','501c3','charitable organization'],
    'Religious Organization': ['church','religious organization','ministry','congregation','diocese'],
    'Municipal Government': ['municipal','city of','county of','municipality','public entity'],
    'Tribal Nation': ['tribal','tribe','native american','indian nation'],
    'Banking': ['bank','banking','credit union','financial institution'],
    'Insurance': ['insurance company','insurer','carrier','underwriting'],
    'Investment Advisory': ['investment advis','registered investment','wealth management','asset management'],
    'Private Equity': ['private equity','venture capital','portfolio company'],
    'Fintech': ['fintech','payment processing','digital payments'],
    'Farming/Ranching': ['farm','ranch','agricultur','livestock','crop'],
    'Agribusiness': ['agribusiness','grain','feedlot','food producer'],
    'Veterinary / Animal Hospital': ['veterinary','veterinarian','animal hospital'],
    'HOA / Community Association': ['homeowners association',' hoa ','community association','condominium association'],
    'Self-Storage Facility': ['self-storage','self storage','storage facility'],
    'Equipment Rental': ['equipment rental','rental equipment','tool rental'],
    'Sports/Recreation': ['sports','recreation','fitness center','gym','athletic'],
    'Venues/Arenas': ['arena','stadium','venue','amphitheater'],
    'Amusement Parks': ['amusement park','theme park','water park'],
  };
  window.SP_VERTICAL_KEYWORDS = SP_VERTICAL_KEYWORDS;   // exposed for the AI path

  /* Phrases that VETO a term hit for a vertical.
   *
   * Some words cannot be removed from a vertical without losing it, but carry a
   * specific innocent sense elsewhere. 'university' is the case that forced
   * this: the University Interscholastic League governs Texas school athletics,
   * so it appears on the paperwork of essentially every Texas high school. A
   * term hit whose surrounding text matches an anti-pattern is not evidence.
   *
   * Checked against the matched occurrence in context, not the whole document,
   * so a genuine university that also runs UIL events still classifies.
   */
  const SP_VERTICAL_ANTI = {
    // A school staffs itself and runs a gym; neither makes it a staffing firm
    // or a sports venue. These veto the ordinary-organisation senses.
    'Staffing/HR': [
      /human\s+resources\s+(?:department|office|director|and\s+student)/i,
      /staffing\s+(?:plan|level|ratio|model|allocation|chart)/i
    ],
    'Sports/Recreation': [
      /(?:middle|elementary|high|intermediate)\s+school[-\s]*(?:gym|gymnasium)/i,
      /\bschool\s+gym(?:nasium)?\b/i,
      // A school's athletics programme is a department, not the business.
      // Aspen View Academy matched on "STUDENT ACTIVITES Athletics Clubs
      // Performing Arts Summer Camp(s)", which is a menu of what students do
      // after class, and on a volleyball practice held in the Gyms.
      /student\s+activit/i,
      /\bathletics?\b(?=[^.]{0,80}\b(?:clubs?|performing\s+arts|student|school|academy|district)\b)/i,
      /\b(?:boys|girls|varsity|junior\s+varsity|jv)\b[^.]{0,40}\b(?:practice|volleyball|basketball|soccer|football|track)\b/i,
      /\binterscholastic\b/i
    ],
    /* A school that had its roof replaced is not a construction company.
     *
     * Tulsa Honor Academy's facilities page: "Over the course of the past
     * several weeks, restoration and construction crews have prioritized
     * ensuring classrooms were repaired and sanitized". The school is the
     * CUSTOMER in that sentence. A construction business describes work it
     * performs — "our crews", "we build", a jobsite — and those terms are
     * untouched here, so a genuine restoration contractor still classifies on
     * the rest of its vocabulary.
     */
    'Construction': [
      // the trade arriving to do work FOR the subject
      /\b(?:restoration|construction|repair|clean-?up|remediation)\s+(?:and\s+\w+\s+)?(?:crews?|teams?|vendors?|companies)\b/i,
      /\bcrews?\s+(?:have|has|had|were|was|will)\b/i,
      // a damage-and-recovery narrative, which is an event, not a line of business
      /\b(?:storm|tornado|hail|wind\s+speeds?|flood|hurricane|damaged?|destruct\w*)\b[^.]{0,120}\b(?:construction|repair|restor\w+|rebuil\w+)\b/i,
      /\b(?:construction|repair|restor\w+|rebuil\w+)\b[^.]{0,120}\b(?:storm|tornado|hail|wind\s+speeds?|flood|hurricane|damaged?|were\s+repaired|sanitiz\w+)\b/i
    ],
    /* "City of Tulsa" is where you are, not what you are.
     *
     * The same page: "a severe weather event with destructive wind speeds
     * occurred throughout the City of Tulsa". A municipality is an insured
     * that has a council, a mayor, a clerk, ordinances and city limits, and
     * says so within a line or two. Absent any of that, "city of X" is an
     * address or a geography — which is exactly what it is on the website of
     * every business that has ever named the town it is in.
     */
    'Municipal Government': [
      ev => /\bcity\s+of\b/i.test(ev)
            && !/\b(council|mayor|city\s+manager|city\s+clerk|municipal\s+court|ordinance|city\s+hall|alderm[ae]n|city\s+limits|incorporated|city\s+attorney|public\s+works\s+department)\b/i.test(ev),
      // "throughout / across / in / serving the City of X" is locative by construction
      /\b(?:throughout|across|within|around|near|greater|serving|located\s+in|based\s+in)\s+(?:the\s+)?city\s+of\b/i
    ],
    /* A charter school explaining itself is not a school district.
     *
     * Every "public school" on Tulsa Honor Academy's site is one of three
     * things: the authorizer's NAME ("the Tulsa Public Schools (TPS) school
     * board voted to approve the THA Charter School"), a definition of what
     * charter schools are ("Charter schools are independently-operated public
     * schools"), or the school a family left ("their assigned public school").
     * None of them is the insured.
     */
    'Public School District': [
      /\bcharter\s+school/i,
      /\b(?:authoriz\w+|sponsor\w*|approved\s+by|school\s+board\s+voted|application)\b/i,
      /\bassigned\s+public\s+school\b/i
    ],
    'Higher Education': [
      // "Parent University Nights" is a K-12 family-engagement programme, named
      // that way on school sites across the country. It is not a university.
      /\b(?:parent|family|community|customer|client)\s+university\b/i,
      /university\s+interscholastic\s+league/i,
      /\buil\b/i,
      /college\s+(?:prep|preparatory|readiness|bound|acceptance|credit|application)/i,
      /university\s+of\s+texas\s+(?:interscholastic|league)/i
    ]
  };
  window.SP_VERTICAL_ANTI = SP_VERTICAL_ANTI;

  function _sourcesText(sources){ return sources.map(s=>s.text||'').join('\n'); }

  // Word-boundary term matching. Bare substring search silently invents
  // evidence: 'peo' (Staffing/HR) matches inside "people", 'isd' inside
  // "misdemeanor" — enough to confirm a vertical on any page with prose on it.
  // Rules: always require a boundary before the term; after it, allow a short
  // inflection ("campus" -> "campuses", "clinic" -> "clinical") only for terms
  // long enough that the suffix can't swallow a different word. Terms under 5
  // chars must match as whole words.
  const _spTermRe = (() => {
    const cache = new Map();
    return term => {
      const t = String(term||'').trim().toLowerCase();
      if (!t) return null;
      if (cache.has(t)) return cache.get(t);
      const esc = t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const head = /^[a-z0-9]/.test(t) ? '\\b' : '';
      const tail = /[a-z0-9]$/.test(t) ? (t.length >= 5 ? '[a-z]{0,2}(?![a-z])' : '(?![a-z])') : '';
      const re = new RegExp(head + esc + tail, 'gi');
      cache.set(t, re);
      return re;
    };
  })();

  // Core anti-hallucination check: return the first source where `term`
  // literally appears, with a surrounding evidence snippet — or null.
  window.spVerifyGrounding = function(terms, sources){
    for (const src of sources){
      const hay = (src.text||'');
      for (const t of terms){
        const re = _spTermRe(t); if(!re) continue;
        re.lastIndex = 0;
        const m = re.exec(hay);
        if (m){
          const at = m.index;
          const start = Math.max(0, at-45), end = Math.min(hay.length, at+m[0].length+45);
          let snippet = hay.slice(start,end).replace(/\s+/g,' ').trim();
          if (start>0) snippet='…'+snippet; if (end<hay.length) snippet+='…';
          return { source: src.label, matched: String(t).trim(), evidence: snippet };
        }
      }
    }
    return null;
  };

  // ===== GATE 1: a SentriPol report is not a source document =====
  // Re-uploading an exported review lets the tool cite its OWN prior output as
  // policy evidence. Observed 2026-08-20: "Base-power-Compact.pdf" (an exported
  // compact report) carried the line "Detected verticals Renewable Energy,
  // SaaS/Software, General Manufacturing" on page 1, which then grounded a
  // SaaS/Software vertical "in the declarations" for a battery/electricity
  // company. Require >=2 fingerprints so a genuine policy that merely names the
  // tool is not thrown away.
  const _SP_SELF_MARKERS = [
    /sentripol/i,
    /is\s+an\s+AI-powered\s+policy\s+review/i,
    /detected\s+verticals/i,
    /naics\s+roll-?up/i,
    /auto-?detected\s+profile/i,
    /not\s+a\s+substitute\s+for\s+professional\s+broker\s+review/i,
    /standalone\s+policies\s+recommended/i
  ];
  window.spIsSelfReport = function(text){
    const t = String(text||'').slice(0, 60000);
    return _SP_SELF_MARKERS.filter(re=>re.test(t)).length >= 2;
  };

  // ===== GATE 2: "declarations" is a provenance CLAIM, so earn it =====
  // Pages 1-2 of a PDF are not automatically a declarations page - the report
  // above is the proof. Only label a slice "declarations:" when it reads like
  // one; everything else is labelled for what it is and weighs far less.
  const _SP_DEC_MARKERS = [/\bdeclarations?\b/i, /named\s+insured/i,
    /policy\s+(?:number|no\.?|period)/i, /information\s+page/i, /\bpolicy\s+eff/i];
  window.spIsDeclarations = function(text){
    const t = String(text||'');
    return _SP_DEC_MARKERS.filter(re=>re.test(t)).length >= 2;
  };

  // Build WEIGHTED evidence sources. The named-insured / declarations pages and
  // the org's own website describe what the org IS and where it operates far more
  // reliably than boilerplate buried deep in a policy form, so they weigh more.
  window.spBuildSources = function(pdfTexts, websiteText, fileNames){
    const sources=[]; const excluded=[];
    (pdfTexts||[]).forEach(doc=>{
      const pages=(doc.pages||[]);
      const full=pages.map(p=>p.text||'').join(' ');
      const name=(doc.fileName||'document');
      if (window.spIsSelfReport(full)){ excluded.push(name); return; }          // GATE 1
      const head=pages.slice(0,2).map(p=>p.text||'').join(' ');
      if (head.trim()){
        const isDec = window.spIsDeclarations(head);                            // GATE 2
        sources.push({ label:(isDec?'declarations: ':'pages 1-2 of ')+name,
                       text:window.spStripTestimonials(head, { headingsOnly:true }),
                       weight:isDec?2.5:1.2, kind:isDec?'declarations':'policy' });
      }
      if (full.trim()) sources.push({ label:'policy: '+name,
                       text:window.spStripTestimonials(full, { headingsOnly:true }),
                       weight:1, kind:'policy' });
    });
    if (websiteText && websiteText.trim()){
      // The Auto-Fetch button GENERATES an overview from the vertical already on
      // screen, so it echoes the vertical back and cannot corroborate it. Tag it
      // so GATE 3 ignores it and it no longer outweighs the policy itself.
      const generated = !!window._spOrgOverviewGenerated;
      const meta = window._siteFetchMeta;
      const label = generated ? 'website/overview (auto-generated placeholder)'
                  : (meta && meta.host) ? ('company website: '+meta.host+' ('+(meta.pages||[]).length+' page(s) fetched)')
                  : 'website/overview';
      // Full strip on the site: this is where the review voice lives, and every
      // downstream reader — profile, vertical, NAICS, BOP — takes its text
      // from here.
      sources.push({ label:label, text:window.spStripTestimonials(websiteText), weight: generated?1:3,
                     kind:'site', independent:!generated, fetched:!!(meta&&meta.host) });
    }
    const keptNames=(fileNames||[]).filter(n=>excluded.indexOf(n)<0);
    if (keptNames.length) sources.push({ label:'file names', text:keptNames.join('  '), weight:2, kind:'filename' });
    window._spExcludedSources = excluded;
    window._spSourceStats = {
      docs: (pdfTexts||[]).length,
      docsUsed: (pdfTexts||[]).length - excluded.length,
      excluded: excluded.length,
      siteChars: sources.filter(x=>x.kind==='site' && x.independent!==false)
                        .reduce((n,x)=>n+String(x.text||'').trim().length, 0),
      siteError: window._siteFetchError || ''
    };
    return sources;
  };

  // ===== GATE 3: cross-check every vertical against the company's own site =====
  // A vertical read off policy paper only says what forms the carrier attached.
  // The company's own description of itself is the independent check. Verticals
  // the site does not corroborate are still shown - never silently dropped - but
  // they are flagged and they lose the headline slot, because verticals[0] is
  // what the rest of the review keys off.
  // WHICH VERTICALS THE REPORT SHOWS.
  //
  // A vertical read off policy paper alone is a guess about the insured made
  // from the carrier's vocabulary: the words in a GL form describe what the
  // FORM covers, not what the business does. Where the company's own website
  // is available, it is the one source that speaks for the insured — so the
  // report displays the verticals the site corroborates and no others.
  //
  // Nothing is deleted: the suppressed ones are returned in `hidden` and named
  // under the list, because "the paper suggests manufacturing and the site
  // never mentions it" is itself a finding. And with no site text at all there
  // is nothing to cross-check against, so the full list stands rather than the
  // section going blank.
  window.spVerticalsForDisplay = function(verticals){
    const list = (verticals || []).slice();
    const haveSite = list.some(v => v.site === 'confirmed' || v.site === 'absent');
    // With nothing to cross-check against, everything used to be shown — which
    // on a paper-only review is the keyword count, unfiltered. If the
    // declarations answered the question, that answer is the list.
    if (!haveSite){
      const dec = list.filter(v => v.declared);
      return dec.length ? { shown: dec, hidden: list.filter(v => !v.declared), haveSite: false }
                        : { shown: list, hidden: [], haveSite: false };
    }
    const _keep = v => v.site === 'confirmed' || v.site === 'declared';
    return { shown: list.filter(_keep),
             hidden: list.filter(v => !_keep(v)),
             haveSite: true };
  };

  window.spSiteCrossCheck = function(verticals, sources){
    const site=(sources||[]).filter(s=>s.kind==='site' && s.independent!==false);
    const haveSite = site.some(s=>String(s.text||'').replace(/\s+/g,' ').trim().length >= 40);
    (verticals||[]).forEach(v=>{
      // With no website read there is normally nothing to cross-check against.
      // A declared trade is the exception: the contract already answered, and
      // "no site info" would understate evidence we hold.
      if(!haveSite){ v.site = v.declared ? 'declared' : 'no-site'; return; }
      // Scored, not merely grounded. spVerifyGrounding answers "does this word
      // appear", which is a weaker question than the scorer asks and bypasses
      // every veto it applies. Tulsa Honor Academy was marked Construction
      // "✓ site" on one sentence about storm repairs — the scorer had already
      // rejected that sentence, and the cross-check confirmed the vertical on
      // it anyway. A cross-check that accepts evidence the scorer threw out is
      // not a check.
      const sv = _spScoreVertical(v.name, site);
      if(sv.hits.length){ v.site='confirmed'; v.siteEvidence=sv.best.evidence; v.siteMatched=sv.best.matched; v.siteScore=sv.score; }
      // A trade the CONTRACT states is not "not on site". The website may never
      // use the word — a school leads with its mission, its policy leads with
      // its class — and hiding the declared answer because the marketing copy
      // phrased it differently is the same mistake in the other direction.
      else if (v.declared){ v.site='declared'; v.siteScore=0; }
      else  { v.site='absent'; v.siteScore=0; }
    });
    const rank=v=>(v.site==='confirmed'?3:(v.site==='declared'?2:(v.site==='no-site'?1:0)));
    return (verticals||[]).slice().sort((a,b)=>rank(b)-rank(a));   // stable: ties keep score order
  };

  // WHO THE PAPER IS FROM IS NOT WHO THE PAPER IS ABOUT.
  //
  // A policy is written by a carrier and placed by a broker, and both put their
  // own name, address and marketing on every page. Read as evidence of the
  // insured's line of business, that produced two systematic false positives on
  // real documents: "Consultants epicbrokers.com" in a broker footer scored
  // Consulting at 80 for an insured that is not a consultancy, and the policy's
  // own vocabulary made Insurance the top vertical on 20 of 22 documents.
  //
  // Both are suppressed on POLICY sources only. On the organisation's own
  // website the same words mean exactly what they say — a real insurance agency
  // calls itself one, and a real consultancy calls itself one.
  // Two regexes, because case matters differently for each half. Policies print
  // their labels in caps ("NAME AND ADDRESS OF AGENT OR BROKER"), so those must
  // match case-insensitively; the carrier-name pattern leans on Title Case to
  // avoid swallowing ordinary prose that happens to end in "insurance company".
  const _SP_BRANDING_I = new RegExp([
    'name\\s+and\\s+address\\s+of\\s+(?:the\\s+)?(?:agent|broker|producer)',
    '\\b(?:agent|broker|producer|agency|sub-?producer)\\s*:',
    'produced\\s+by|countersigned\\s+by|underwritten\\s+by',
    '[A-Za-z0-9-]*(?:brokers?|insurance|agency|underwriters|risk|benefits)[A-Za-z0-9-]*\\.(?:com|net|org)',
  ].join('|'), 'gi');
  const _SP_BRANDING_S = new RegExp(
    '[A-Z][A-Za-z&.\'\\- ]{2,40}(?:Insurance\\s+Compan(?:y|ies)|Insurance\\s+Co\\.|Indemnity\\s+Compan(?:y|ies)|' +
    'Casualty\\s+Compan(?:y|ies)|Mutual\\s+Insurance|Underwriters|Assurance\\s+Compan(?:y|ies)|Insurance\\s+Group)', 'g');

  function _spBrandingSpans(text){
    const spans=[]; let m;
    for (const rx of [new RegExp(_SP_BRANDING_I.source,'gi'), new RegExp(_SP_BRANDING_S.source,'g')]){
      while((m=rx.exec(text))!==null) spans.push([Math.max(0,m.index-120), m.index+m[0].length+320]);
    }
    const merged=[];
    spans.sort((a,b)=>a[0]-b[0]).forEach(sp=>{
      if (merged.length && sp[0]<=merged[merged.length-1][1]) merged[merged.length-1][1]=Math.max(merged[merged.length-1][1], sp[1]);
      else merged.push([sp[0], sp[1]]);
    });
    return merged;
  }

  // Terms that name the insurance transaction itself rather than anyone's line
  // of business. On paper they describe the counterparty, always.
  const _SP_SELF_REFERENTIAL = /^(insurance\s+compan(?:y|ies)|insurer|carrier|underwriting|policyholder|named\s+insured)$/i;

  // A coverage form lists the professions whose fees it will REIMBURSE — "the
  // fees and expenses of an independent public relations consultant", "the
  // reasonable and necessary consultant costs". Those name what the policy
  // buys, not what the insured does, and on the Cyber form alone they were
  // enough to classify an insured as a consultancy. A vertical term sitting
  // inside a payment clause or a definition is the form's vocabulary.
  const _SP_GRANT_BEFORE = /(fees?\s+and\s+expenses\s+of|we\s+will\s+(?:pay|reimburse)|reasonable\s+and\s+necessary|costs?\s+(?:of|or\s+expenses)|with\s+respect\s+to|means\s+the|shall\s+mean|does\s+not\s+include|arising\s+out\s+of\s+the\s+rendering)/i;

  /* FORM LANGUAGE IS NOT A DESCRIPTION OF THE INSURED.
   *
   * Tulsa Honor Academy — a charter school — was reported as Construction at
   * score 28.5, second only to its actual line of business. Every one of the
   * ~40 occurrences across its four policies is the form talking about itself:
   *
   *   "Increased Cost of Construction"                 (ordinance-or-law extension)
   *   "Course of Construction/Renovation Endorsement"  (an endorsement title)
   *   "OWNERS AND CONTRACTORS PROTECTIVE LIABILITY COVERAGE"
   *   "Design, specifications, workmanship, repair, construction, renovation,
   *    remodeling, grading"                            (an exclusion's list)
   *   "(2) Road construction or resurfacing equipment" (a mobile-equipment definition)
   *   "Structural alterations, new construction or demolition operations"
   *
   * Not one says the insured builds anything. _SP_GRANT_BEFORE already caught
   * the "cost of" shape and nothing else, because it looked 80 characters back
   * for one of nine phrases. The vocabulary of a coverage form is much larger
   * than that, so these match on STRUCTURE instead: a coverage/exclusion
   * scaffold near the term, a numbered clause, a list of activities, or a
   * title line.
   *
   * Policy paper only. A contractor's own declarations say "Business
   * Description: General Contractor" and a class code says "Contractors —
   * subcontracted work"; neither carries any of this scaffolding, so a real
   * construction risk still classifies as one.
   */
  const _SP_FORM_SENSE = new RegExp([
    // coverage and exclusion scaffolding
    'increased cost of', 'course of construction', 'ordinance or law', 'sublimit',
    'endorsement', 'exclusion', 'coverage (?:part|form|extension|agreement)',
    'additional coverage', 'we will (?:not )?pay', 'this insurance does not apply',
    'does not apply to', 'is amended', 'is (?:deleted|replaced|added)',
    'as used in this', 'the following is added', 'subject to the',
    'arising out of', 'caused by or resulting from', 'with respect to',
    'your work', 'your product', 'insured contract', 'protective liability',
    // definition wording
    '(?:means|shall mean|includes but is not limited to)\\b',
    // list markers: "(2) Road construction", "a. We will not pay"
    '\\(\\s*[0-9a-z]\\s*\\)', '^\\s*[0-9a-z]\\s*\\.\\s',
  ].join('|'), 'i');

  // Three or more comma-separated short items around the term: an enumeration
  // of activities in an exclusion, never a sentence about a business.
  const _SP_ENUM_RUN = /(?:\b[a-z][a-z-]{2,20},\s+){3,}/i;

  // A title line: mostly capitals, carrying a coverage noun. Endorsement and
  // coverage-part titles are printed this way on every carrier's paper.
  const _SP_FORM_TITLE = /\b(COVERAGE|ENDORSEMENT|EXCLUSION|SCHEDULE|DECLARATIONS|FORM|LIABILITY|SUPPLEMENTAL)\b/;
  function _spLooksLikeFormTitle(line){
    const letters = line.replace(/[^A-Za-z]/g, '');
    if (letters.length < 12) return false;
    const caps = line.replace(/[^A-Z]/g, '').length;
    return caps / letters.length > 0.6 && _SP_FORM_TITLE.test(line);
  }

  /* The insurance register. These words do not occur in a sentence about a
   * business; they occur in a sentence about a policy. Their presence beside a
   * vertical keyword is the broadest signal available that the document has
   * stopped describing the insured and started describing the cover.
   *
   * On their own they would be too broad — a declarations page says "Named
   * Insured" two lines above the business description — which is why the
   * field-label test below OVERRIDES them.
   */
  const _SP_POLICY_REGISTER = new RegExp([
    '\\b(?:any|the|named|additional)\\s+insured\\b', '\\binsured\\s+(?:property|premises|location)\\b',
    '\\bat\\s+the\\s+time\\s+of\\s+loss\\b', '\\b(?:aggregate|each\\s+occurrence|per\\s+occurrence)\\s+limit\\b',
    '\\blimit\\s+of\\s+insurance\\b', '\\bpolicy\\s+period\\b', '\\bdeductible\\b',
    '\\bcovered\\s+(?:location|property|premises|construction|auto|pollution)\\b',
    '\\bon\\s+your\\s+behalf\\b', '\\bthat\\s+particular\\s+part\\b',
    '\\bthis\\s+(?:endorsement|policy|insurance|coverage|provision)\\b',
    '\\bapply\\s+to\\s+work\\s+for\\s+us\\b', '\\bthis\\s+information\\b',
    '\\bthe\\s+building\\s+containing\\b', '\\breal\\s+property\\b',
  ].join('|'), 'i');

  /* …except where the term sits in a DECLARATIONS FIELD.
   *
   * A form describes cover; a declarations page describes the insured, and it
   * does so in labelled fields. "Business Description: General Contractor" is
   * the insured's own trade stated by the carrier, and it is the one place on
   * policy paper where a vertical keyword means what it says. This test wins
   * over every veto below it.
   */
  const _SP_DEC_FIELD = /\b(?:business\s+(?:description|of\s+the\s+named\s+insured)|description\s+of\s+(?:business|operations)|nature\s+of\s+business|operations?|classification|class\s+code|occupancy|insured'?s?\s+business|trade|industry|naics|sic)\b[^\n]{0,24}[:\-\u2013]\s*$/i;

  /** Is this occurrence the FORM speaking, rather than the document describing
   *  the insured? `hay` and `at` locate it; the window is wide enough to see a
   *  clause it sits inside and narrow enough not to reach the next one. */
  /* The other place paper describes the business: a sentence whose subject is
   * the insured and whose verb is an activity. "The insured operates as a
   * general contractor" is a description; "any insured or any contractors"
   * is the damage-to-property exclusion. Both contain "insured", which is why
   * the register test alone was vetoing the first along with the second.
   */
  const _SP_INSURED_PREDICATE =
    /\b(?:the\s+)?(?:named\s+)?insured\s+(?:also\s+|primarily\s+|principally\s+|currently\s+|additionally\s+)?(?:is\s+(?:an?\s+)?|are\s+(?:an?\s+)?|operates?|performs?|provides?|conducts?|manufactures?|owns|is\s+engaged\s+in)\b/i;

  function _spIsFormSense(hay, at, len){
    // A labelled declarations field is the insured's own trade. Checked first
    // and unconditionally, or the register test below would veto the one
    // position on policy paper that is genuinely about the business.
    if (_SP_DEC_FIELD.test(hay.slice(Math.max(0, at - 70), at))) return false;
    if (_SP_INSURED_PREDICATE.test(hay.slice(Math.max(0, at - 120), at))) return false;
    const lineStart = hay.lastIndexOf('\n', at) + 1;
    let lineEnd = hay.indexOf('\n', at); if (lineEnd < 0) lineEnd = hay.length;
    const line = hay.slice(lineStart, lineEnd);
    if (_spLooksLikeFormTitle(line)) return true;
    const around = hay.slice(Math.max(0, at - 140), Math.min(hay.length, at + len + 140));
    return _SP_FORM_SENSE.test(around) || _SP_ENUM_RUN.test(around)
           || _SP_POLICY_REGISTER.test(around);
  }

  /* A NEWS STORY IS NOT A DESCRIPTION OF THE BUSINESS EITHER.
   *
   * The same school's website supplied the corroboration that let Construction
   * and Municipal Government through the site cross-check:
   *
   *   "…restoration and construction crews have prioritized ensuring
   *    classrooms were repaired and sanitized…"
   *   "…a severe weather event with destructive wind speeds occurred
   *    throughout the City of Tulsa…"
   *
   * Both are one storm-damage item on /about-us/news. A school that has a roof
   * replaced is not a construction company, and a school in a city is not a
   * municipality. This is the same reasoning as _SP_EVENT_LINE for calendars,
   * applied at page level: the fetched text carries an "[url]" marker per page,
   * so the page a hit sits on is recoverable.
   *
   * Dropped only when other evidence survives — a construction company's news
   * page is still about construction, and it will have said so elsewhere too.
   */
  /* A MENU OF POSSIBILITIES IS NOT AN IDENTITY.
   *
   * Tulsa Honor Academy's overview explains Oklahoma charter law: "charter
   * schools can be authorized by the following entities: a local school
   * district or technology center, a comprehensive or regional institution
   * that is a member of The Oklahoma State System of Higher Education, a
   * federally recognized tribe, or the State Board of Education."
   *
   * One sentence, and it supplied the school's "school district" hit, both its
   * "higher education" hits and a "university" hit. It is a list of bodies
   * that COULD authorize a charter — the insured is none of them. The same
   * shape is everywhere: "we partner with hospitals, universities and
   * municipalities", "accredited by…", "a member of…".
   *
   * Applies to every vertical and to both sources, and like the calendar and
   * news rules it only bites when other evidence survives — an organisation
   * that is genuinely one of the listed things will have said so plainly
   * somewhere that is not a list.
   */
  const _SP_ENTITY_NOUN = '(?:district|agency|university|college|school|tribe|board|institution|' +
                          'center|centre|organization|organisation|municipality|corporation|' +
                          'authority|commission|department|nonprofit|foundation|association)';
  const _SP_MENU_SENSE = new RegExp([
    '(?:authoriz|sponsor|accredit|charter|govern|regulat|oversee|approv|licens)\\w*\\s+by\\b',
    '\\bmembers?\\s+of\\b',
    '\\bthe\\s+following\\s+entit',
    '\\b(?:such\\s+as|one\\s+of|either)\\b',
    // an "or"/"and" list of two institution types inside one sentence
    _SP_ENTITY_NOUN + '[^.]{0,70}\\b(?:or|and)\\b[^.]{0,70}' + _SP_ENTITY_NOUN,
  ].join('|'), 'i');

  const _SP_NEWS_PATH = /\/(news|announcements?|blog|stories|story|press|media|updates?|newsroom)(\/|$|\?)/i;
  function _spOnNewsPage(hay, at){
    const marker = hay.lastIndexOf('\n[http', at);
    if (marker < 0) return false;
    let end = hay.indexOf(']', marker); if (end < 0 || end > at) return false;
    return _SP_NEWS_PATH.test(hay.slice(marker + 2, end));
  }

  const _SP_BRAND_CACHE = new WeakMap();          // one scan per source, not per vertical
  function _spBrandSpansFor(src){
    if (src.kind === 'site') return [];
    let v = _SP_BRAND_CACHE.get(src);
    if (!v){ v = _spBrandingSpans(src.text||''); _SP_BRAND_CACHE.set(src, v); }
    return v;
  }

  // Collect EVERY occurrence of any term across weighted sources (with snippet).
  function _spFindAll(terms, sources){
    const hits=[];
    for (const src of sources){
      const hay=(src.text||'');
      const onPaper=src.kind && src.kind!=='site';
      const brand=_spBrandSpansFor(src);
      for (const t of terms){
        if (onPaper && _SP_SELF_REFERENTIAL.test(String(t).trim())) continue;
        const re=_spTermRe(t); if(!re) continue;
        re.lastIndex=0;
        let m, n=0;
        while((m=re.exec(hay))!==null && n<25){
          const at=m.index;
          if (onPaper && (brand.some(sp=>at>=sp[0] && at<=sp[1])
                          || _SP_GRANT_BEFORE.test(hay.slice(Math.max(0,at-80), at))
                          || _spIsFormSense(hay, at, m[0].length))){
            if(re.lastIndex===at) re.lastIndex++;
            continue;                              // carrier/broker branding, or the form's own wording
          }
          const start=Math.max(0,at-55), end=Math.min(hay.length,at+m[0].length+55);
          let snip=hay.slice(start,end).replace(/\s+/g,' ').trim();
          if(start>0) snip='…'+snip; if(end<hay.length) snip+='…';
          hits.push({ label:src.label, weight:src.weight||1, matched:String(t).trim(),
                      evidence:snip, news: !onPaper && _spOnNewsPage(hay, at) });
          if(re.lastIndex===at) re.lastIndex++;   // zero-length guard
          n++;
        }
      }
    }
    return hits;
  }
  // A multi-word trigger ("independent school district") is far more diagnostic
  // of the insured's actual line of business than a bare word ("university").
  const _spSpecificity = t => (String(t).trim().split(/\s+/).length>=2 ? 2 : 1);

  // How many occurrences of one term can count. Three is enough to separate a
  // term the page actually uses from a single stray mention, without letting a
  // calendar or a footer repeated on every page decide the vertical.
  const _SP_REPEAT_CAP = 3;

  /* An events calendar is not a description of the business.
   *
   * School sites list rooms and activities over and over: "5:30 PM - 7:30 PM
   * Middle School- Gym", "Wednesday, September 9 ACA PTO Fundraising". Those
   * name where something happens, not what the organisation is. The same is
   * true of a gym in a hotel or an apartment building.
   */
  const _SP_EVENT_LINE = /\d{1,2}\s*:\s*\d{2}\s*(?:AM|PM)\s*[-\u2013]\s*\d{1,2}\s*:\s*\d{2}\s*(?:AM|PM)|\b(?:Mon|Tues|Wednes|Thurs|Fri|Satur|Sun)day,\s*[A-Z][a-z]+\s+\d{1,2}\b/i;
  // Contexts where a state name is boilerplate, not the org's operating state.
  const _SP_STATE_BOILERPLATE = /all states|other states|except|any (?:one )?state|states? in which|listed below|3\.a\.|shown in the schedule/i;

  /* THE ANSWER IS PRINTED ON THE DECLARATIONS PAGE.
   *
   * Three of Tulsa Honor Academy's four policies say what it is, in a labelled
   * field, on the first page:
   *
   *   Auto Policy      BUSINESS DESCRIPTION               Charter School
   *   Package Policy   Business Description:              Charter School
   *   Excess Policy    Business of the Named Insured is:  Charter School
   *
   * The classifier was counting words across 200 pages instead, and reported
   * Construction — a score assembled entirely from exclusion wording. Every
   * rule added above this one exists to stop frequency from reaching a wrong
   * answer. This reads the right one directly.
   *
   * A declared description is the carrier's own statement of the insured's
   * trade, underwritten and printed on the contract. It is stronger evidence
   * than anything a keyword count can produce, and stronger than the website —
   * a school's homepage leads with its mission, its policy leads with its
   * class. So it takes authority over both.
   */
  const _SP_DEC_LABELS = [
    'business\\s+description',
    'business\\s+of\\s+the\\s+(?:named\\s+)?insured(?:\\s+is)?',
    'nature\\s+of\\s+(?:the\\s+)?business',
    'description\\s+of\\s+(?:business|operations?|the\\s+insured)',
    "insured'?s?\\s+(?:business|operations?)",
    'type\\s+of\\s+business',
    'business\\s+type',
    'occupancy(?:\\s+description)?',
    'classification(?:\\s+description)?',
    'class\\s+description',
    'operations?\\s+description',
  ];
  // A label, then the value: on the same line after a colon or a run of
  // whitespace (fixed-width declarations), or in the next pipe cell (a
  // LlamaParse markdown table), or on the next line (a stacked label).
  const _SP_DEC_LINE = new RegExp(
    // Captured WIDER than the 80-character reject below. With both at 80 the
    // capture simply stopped at the limit, so no value was ever long enough
    // to be rejected and a runaway line came back as an 80-character "trade".
    // An item marker may precede the label. Excess and management-liability
    // declarations number their fields — "Item 2. Business of the Named
    // Insured is: Charter School", "(E) Pending or Prior Proceedings Date" —
    // and requiring the label to START the line skipped every one of them.
    // Found by a corpus fixture, not by a client.
    '(?:^|\\n|\\|)\\s*\\|?\\s*(?:item\\s+\\d+[.)]?\\s*|\\([A-Za-z0-9]{1,3}\\)\\s*|\\d{1,2}[.)]\\s*)?(?:' + _SP_DEC_LABELS.join('|') + ')\\s*\\|?\\s*[:\\-\\u2013]?\\s*\\|?[ \\t]*([^\\n|]{0,140})',
    'gi');

  //: NAICS/SIC prefixes worth reading. Deliberately short — a code is only
  //: useful here when it maps to a vertical this product actually has, and a
  //: wrong expansion of an unfamiliar prefix is worse than not reading it.
  const _SP_CODE_VERTICAL = [
    [/^6111[12]/, 'Public School District'], [/^61111/, 'Public School District'],
    [/^6113/, 'Higher Education'],   [/^8221/, 'Higher Education'],
    [/^6244/, 'Early Childhood/Daycare'], [/^8351/, 'Early Childhood/Daycare'],
    [/^23[0-9]/, 'Construction'],    [/^15[0-9]{2}/, 'Construction'],
    [/^622/, 'Hospital/Health System'], [/^8062/, 'Hospital/Health System'],
    [/^6216|^6231/, 'Senior Living / Long-Term Care'], [/^8051|^8059/, 'Senior Living / Long-Term Care'],
    [/^484/, 'Trucking/Fleet'],       [/^4213/, 'Trucking/Fleet'],
    [/^7225|^5812/, 'Restaurants/Bars'], [/^7211|^7011/, 'Hotels/Resorts'],
    [/^5417|^5416/, 'Consulting'],    [/^5411|^8111/, 'Law Firm'],
    [/^5412|^8721/, 'Accounting Firm'], [/^5413|^8711/, 'Architecture/Engineering'],
    [/^5221|^602[012]/, 'Banking'],    [/^5241|^63[0-9]{2}/, 'Insurance'],
    [/^31[0-9]|^32[0-9]|^33[0-9]/, 'General Manufacturing'],
    [/^9[1-7][0-9]{2}/, 'Municipal Government'],   // SIC public administration; 99xx is "unclassified", not a trade
  ];
  const _SP_CODE_LINE = /\b(?:naics|sic)\b[^0-9\n]{0,24}([0-9]{4,6})/gi;

  //: Values that are a form-of-business or a placeholder, not a trade.
  //: One of the labels above, repeated at the start of a value. See the
  //: stacked-label case in spReadBusinessDescription.
  const _SP_INNER_LABEL = new RegExp('^\\s*(?:' + _SP_DEC_LABELS.join('|') + ')\\s*[:\\-\\u2013]\\s*', 'i');

  //: A value that is plainly another field, not a trade. These come from the
  //: looser labels ("operations", "occupancy") landing on a premises schedule,
  //: where every row begins "Number: 001 <name> Limit Of Insurance". Reading
  //: one of those as the insured's business would send a building's name to
  //: the classifier as its industry.
  const _SP_FIELD_NOISE = /\b(?:limit\s+of\s+insurance|limits?\s+of\s+liability|deductible|premium|policy\s+number|effective\s+date|coverage\s+part|sub-?limit)\b|^(?:number|item|loc(?:ation)?|bldg|building)\b|^(?:is|are|was|were|and|or|the)\b/i;

  //: Values that are a form-of-business, a placeholder, or a bare generic
  //: noun rather than a trade. "Form Of Business: Corporation" says how the
  //: insured is incorporated, which is not what it does.
  const _SP_NOT_A_TRADE = /^(?:n\/?a|none|see\s.*|refer\s+to\s.*|various|as\s+(?:per|shown|scheduled)|blank|tbd|business(?:es)?|operations?|corporation|corp\.?|llc|l\.l\.c\.|inc\.?|incorporated|company|co\.?|partnership|individual|sole\s+proprietor\w*|joint\s+venture|trust|association|entity|organization|limited\s+liability\s+\w+|[-–—.\s]*)$/i;

  // Same snippet shape the state reader uses; that one is scoped inside
  // spDetectProfile, and this runs before it.
  const _spSnip = (hay, at, len) => {
    const a=Math.max(0,at-45), b=Math.min(hay.length, at+len+45);
    return (a>0?'…':'')+hay.slice(a,b).replace(/\s+/g,' ').trim()+(b<hay.length?'…':'');
  };

  /** Every business description STATED in a labelled declarations field.
   *  Policy sources only: a website has no declarations page. */
  function spReadBusinessDescription(sources){
    const out = [];
    (sources || []).forEach(src => {
      if (src.kind === 'site') return;
      const hay = String(src.text || '');
      _SP_DEC_LINE.lastIndex = 0;
      let m;
      while ((m = _SP_DEC_LINE.exec(hay)) !== null){
        let val = String(m[1] || '').replace(/\s+/g, ' ').trim().replace(/[|*_]+$/, '').trim();
        // A stacked label ("Business Description:" alone on its line) puts the
        // value on the line below. Fixed-width declarations do this whenever
        // the field is wide.
        if (!val){
          const nl = hay.indexOf('\n', m.index + m[0].length);
          if (nl >= 0){
            let nx = hay.indexOf('\n', nl + 1);
            val = hay.slice(nl + 1, nx < 0 ? hay.length : nx)
                     .replace(/[|]/g, ' ').replace(/\s+/g, ' ').trim();
          }
        }
        // A stacked label often sits above ANOTHER label rather than above a
        // value. Matrix's package policy prints
        //
        //     DESCRIPTION OF BUSINESS
        //     Business Description: Non-Profit with Social Service Agencies
        //
        // so the outer label's "next line" is the inner label and its value.
        // Strip the inner label rather than reporting the trade as
        // "Business Description: Non-Profit with Social Service Agencies".
        val = val.replace(_SP_INNER_LABEL, '').trim();
        if (!val || val.length > 80 || _SP_NOT_A_TRADE.test(val) || _SP_FIELD_NOISE.test(val)) continue;
        out.push({ value: val, label: src.label,
                   evidence: _spSnip(hay, m.index, Math.min(m[0].length, 110)) });
      }
      _SP_CODE_LINE.lastIndex = 0;
      while ((m = _SP_CODE_LINE.exec(hay)) !== null){
        out.push({ code: m[1], label: src.label,
                   evidence: _spSnip(hay, m.index, m[0].length) });
      }
    });
    return out;
  }
  window.spReadBusinessDescription = spReadBusinessDescription;

  /** Declared description -> vertical. Returns Map(name -> evidence record).
   *
   *  The value is matched against the vertical keyword table with the SAME
   *  term matcher the scorer uses, and the longest matching term wins, so
   *  "Charter School" resolves to Charter School and not to Public School
   *  District on the word "school". */
  function spDeclaredVerticals(sources){
    const found = new Map();
    spReadBusinessDescription(sources).forEach(rec => {
      if (rec.code){
        const hit = _SP_CODE_VERTICAL.find(([re]) => re.test(rec.code));
        if (hit && !found.has(hit[1])){
          found.set(hit[1], { ...rec, matched: rec.code, via: 'code' });
        }
        return;
      }
      let best = null;
      Object.keys(SP_VERTICAL_KEYWORDS).forEach(name => {
        (SP_VERTICAL_KEYWORDS[name] || [name]).forEach(term => {
          const re = _spTermRe(term); if (!re) return;
          re.lastIndex = 0;
          if (re.test(rec.value) && (!best || term.length > best.term.length)){
            best = { name, term };
          }
        });
      });
      if (best && !found.has(best.name)){
        found.set(best.name, { ...rec, matched: best.term, via: 'field' });
      }
    });
    return found;
  }
  window.spDeclaredVerticals = spDeclaredVerticals;

  /* WHAT AN ORGANISATION CANNOT SIMULTANEOUSLY BE.
   *
   * Tulsa Honor Academy's site says its curriculum "is key to a college
   * preparatory school", which is a description of the teaching, not of the
   * ownership. With Charter School declared on three policies, Private School
   * is not a second line of business — it is the same one, read through a
   * looser word.
   *
   * These groups are mutually exclusive at the level a broker places cover: an
   * organisation has ONE of them. They are used only where the declarations
   * settle the question, so this never guesses between two undeclared
   * candidates — it just stops a declared answer from being contradicted by a
   * keyword. A group member that is ITSELF declared is never suppressed, so a
   * genuine district that also declares a charter keeps both.
   */
  /* WHAT AN ORGANISATION *IS* AND WHAT IT *DOES* ARE DIFFERENT AXES.
   *
   * Matrix Human Services' declarations say "Business Description: Non-Profit
   * with Social Service Agencies". Its website says early childhood education.
   * Its NAICS is 624410, Child Day Care Services. The report printed all three
   * as competing verticals and left the reader to work out that they do not
   * compete at all: "non-profit" is a tax and governance status, and a
   * non-profit Head Start's INDUSTRY is child day care.
   *
   * A status is still worth reporting — it changes D&O, it changes who the
   * insureds are, it changes the exposure — but it must not be mistaken for
   * the line of business, and it must not be what names the industry for the
   * rest of the report.
   */
  const SP_STATUS_VERTICALS = new Set(['501(c)(3) Nonprofit']);
  window.SP_STATUS_VERTICALS = SP_STATUS_VERTICALS;

  const SP_VERTICAL_FAMILIES = [
    ['Charter School', 'Public School District', 'Private School', 'Higher Education',
     'Early Childhood/Daycare'],
    ['Hospital/Health System', 'Senior Living / Long-Term Care', 'Physician Group / Clinic'],
    ['Banking', 'Insurance', 'Investment Advisory', 'Private Equity', 'Fintech'],
    ['Municipal Government', 'Tribal Nation', '501(c)(3) Nonprofit'],
  ];
  window.SP_VERTICAL_FAMILIES = SP_VERTICAL_FAMILIES;

  /** Names that a declared vertical rules out. Empty unless something was
   *  actually declared. */
  function spFamilySuppressed(declared){
    const out = new Set();
    SP_VERTICAL_FAMILIES.forEach(fam => {
      // A STATUS never rules anything out. "501(c)(3) Nonprofit" sits in a
      // family with Municipal Government and Tribal Nation because those three
      // are genuinely alternative entity types — but a declared nonprofit
      // saying nothing about its trade must not silence the trade, and before
      // this it suppressed nothing only by luck of which names shared a group.
      const won = fam.filter(n => declared.has(n) && !SP_STATUS_VERTICALS.has(n));
      if (!won.length) return;
      fam.forEach(n => { if (!declared.has(n)) out.add(n); });
    });
    return out;
  }
  window.spFamilySuppressed = spFamilySuppressed;

  // INTELLIGENT mapping: score candidates by weighted evidence, then rank and
  // cap — verticals to the top 1–4, states to the top 1–2 — while keeping every
  // returned item grounded in a real, quoted source occurrence.
  // Weighted evidence score for ONE vertical. Shared by the heuristic path and
  // by the floor applied to the LLM's picks, so a confidence of 50 means the
  // same thing whichever path produced the list.
  function _spScoreVertical(name, sources){
    const terms=(SP_VERTICAL_KEYWORDS[name] || [name]);
    const anti=(SP_VERTICAL_ANTI[name] || []);
    let hits=_spFindAll(terms, sources);
    // Drop occurrences whose own context gives the term an innocent meaning.
    // An anti-pattern may be a regex or a predicate. The predicate form exists
    // because some senses are defined by what is ABSENT: "City of Tulsa" is a
    // municipality only when something municipal is nearby, and no regex over
    // the snippet can express that on its own.
    const _vetoed = (a, ev) => (typeof a === 'function' ? a(ev) : a.test(ev));
    if (anti.length) hits = hits.filter(h => !anti.some(a => _vetoed(a, h.evidence || '')));
    // Unconditional, unlike the calendar and news rules below. Those keep a
    // vertical whose whole evidence sits in that context, because a sports
    // venue really can be nothing but a schedule. A list of OTHER entities is
    // different in kind: it is about other entities by definition, so a
    // vertical evidenced only that way is evidenced not at all.
    hits = hits.filter(h => !_SP_MENU_SENSE.test(h.evidence || ''));
    if (!hits.length) return { score:0, hits:[], best:null };
    // Calendar entries name venues and activities, not the business. Dropped for
    // every vertical, and only when other evidence survives — a genuine sports
    // venue whose page is nothing but a schedule keeps its hits.
    const offCalendar = hits.filter(h => !_SP_EVENT_LINE.test(h.evidence || ''));
    if (offCalendar.length) hits = offCalendar;
    // Same rule, one level up: a news item reports what happened to the
    // organisation, not what it does. Dropped only when other evidence
    // survives, so a business whose news page is genuinely about its own trade
    // keeps it.
    const offNews = hits.filter(h => !h.news);
    if (offNews.length) hits = offNews;
    /* A SERVICES LIST IS NOT A LIST OF BUSINESSES.
     *
     * Matrix Head Start "supports the entire family with nutritional, medical,
     * dental, mental health, family community partnerships and much more".
     * That one sentence reported the insured as a Dental Practice and as
     * Behavioral Health. A Head Start screens and refers; it does not run a
     * surgery. _SP_ENUM_RUN already recognised this shape inside a policy
     * exclusion — the shape is the same in marketing copy.
     *
     * Conditional, like the calendar and news rules: a dental group whose page
     * is one long list of the procedures it performs keeps its hits, because
     * for that insured there is no other kind of evidence to fall back to.
     */
    const offList = hits.filter(h => !_SP_ENUM_RUN.test(h.evidence || ''));
    if (offList.length) hits = offList;

    if (!hits.length) return { score:0, hits:[], best:null };
    let score=0; const distinct=new Set();
    /* Repetition is not corroboration.
     *
     * Arlington Classics Academy: its own nav says "What is a Charter School"
     * twice, and its events calendar says "Gym" seven times as the location of
     * PTO meetings. Seven hits of one word beat two hits of a two-word phrase,
     * so the school was reported as Sports/Recreation at score 24.7.
     *
     * One word repeated is one fact, however many times a calendar prints it.
     * So each DISTINCT term contributes at most _SP_REPEAT_CAP occurrences, and
     * the existing distinct-trigger bonus continues to reward an organisation
     * that matches a vertical several different ways. That is the thing worth
     * rewarding: breadth of evidence, not a recurring venue label.
     */
    const perTerm = {};
    hits.forEach(h=>{
      const k = h.matched;
      perTerm[k] = (perTerm[k] || 0) + 1;
      if (perTerm[k] > _SP_REPEAT_CAP) return;
      score += (h.weight||1) * _spSpecificity(k);
      distinct.add(k);
    });
    score += (distinct.size-1) * 1.5;                   // several distinct triggers = stronger
    const best = hits.slice().sort((a,b)=>(b.weight*_spSpecificity(b.matched))-(a.weight*_spSpecificity(a.matched)))[0];
    return { score, hits, best };
  }
  window.spScoreVertical = _spScoreVertical;

  window.spDetectProfile = function(sources){
    // ----- Verticals: weighted score → confidence → rank → cap 4 -----
    // WHAT THE ORGANISATION DOES IS SAID BY THE ORGANISATION.
    //
    // The same rule as operating states, for the same reason. A policy names a
    // trade for every reason except the insured's: "covered construction
    // project" is a designated-project aggregate, "Contractors Insurance" is a
    // warranty condition, "any insured or any contractors or subcontractors"
    // is the damage-to-property exclusion, and "employee or independent
    // contractor" is in the carrier's privacy notice. All four are in Tulsa
    // Honor Academy's package policy, and together they made a charter school
    // the strongest Construction risk in the book.
    //
    // Paper still SCORES — a paper-only vertical is worth naming, and the card
    // names it — but a vertical the site evidences outranks one it does not,
    // whatever the paper says. Where no site was read, paper ranks as before,
    // because there is nothing else to rank by.
    const _vSite=(sources||[]).filter(s=>s.kind==='site' && s.independent!==false);
    const _vHaveSite=_vSite.some(s=>String(s.text||'').replace(/\s+/g,' ').trim().length >= 40);
    // What the declarations SAY, before anything is counted.
    const _declared = spDeclaredVerticals(sources);
    // A declared trade rules out its incompatible siblings. Nothing is ruled
    // out when nothing was declared.
    const _ruledOut = spFamilySuppressed(_declared);
    const vScored=[];
    for (const name of Object.keys(SP_VERTICAL_KEYWORDS)){
      if (_ruledOut.has(name)) continue;
      const { score, hits, best } = _spScoreVertical(name, sources);
      const dec = _declared.get(name) || null;
      // A declared vertical is reported even when no keyword anywhere hit it.
      // "Business Description: Charter School" is the whole evidence needed,
      // and requiring the word to also appear in running text would be asking
      // frequency to ratify the field that overrules it.
      if (!hits.length && !dec) continue;
      const sv = _vHaveSite ? _spScoreVertical(name, _vSite) : { score:0, hits:[], best:null };
      const b = sv.best || best;
      vScored.push({ name, score, siteScore:sv.score, onSite:sv.hits.length>0,
                     declared: dec,
                     hitCount:hits.length,
                     evidence: dec ? dec.evidence : (b ? b.evidence : ''),
                     source:   dec ? dec.label    : (b ? b.label : ''),
                     matched:  dec ? dec.matched  : (b ? b.matched : name) });
    }
    /* Three tiers, in order of how directly the evidence answers the question.
     *
     *   2  the declarations STATE the trade          "Business Description: Charter School"
     *   1  the organisation's own site evidences it
     *   0  a keyword count over policy paper
     *
     * Tier 0 is what produced Construction for a charter school. It still
     * runs, and a tier-0 vertical is still named in the card's "Not shown"
     * line, but it cannot outrank a field that says the answer.
     */
    // A declared STATUS ranks below a declared industry: it is well evidenced
    // and it is not an answer to "what does this organisation do".
    const _vTier = v => (SP_STATUS_VERTICALS.has(v.name) ? (v.declared || v.onSite ? 1 : 0)
                                                        : (v.declared ? 2 : (v.onSite ? 1 : 0)));
    vScored.sort((a,b)=>(_vTier(b)-_vTier(a))
                        || ((b.siteScore||0)-(a.siteScore||0))
                        || (b.score-a.score));
    // A real maximum, not the first element. The list is sorted by TIER now,
    // so vScored[0] is the best-evidenced vertical and not the highest-scoring
    // one — and a declared vertical with no keyword hits scores 0, which made
    // vMax 0 and every confidence below it 0.
    const vMax = vScored.reduce((m,v)=>Math.max(m, v.score||0), 0);
    // Confidence normalises the raw weighted score against the strongest
    // candidate, so one cut-off means the same thing on a two-page dec sheet
    // and a fifty-page benefits guide. A vertical must reach 50 — at least half
    // the evidence of the leading one — before it is reported. The absolute
    // floor stays: a single passing mention is not a line of business no matter
    // how little else the document says.
    // Normalised within the best tier present, so the denominator is always a
    // vertical of the same evidential kind. Otherwise a paper-only vertical
    // scoring 14.5 sets the denominator and the insured's actual, DECLARED
    // line of business reports as 97%.
    const _vTopTier = vScored.length ? _vTier(vScored[0]) : 0;
    /* EACH TIER IS NORMALISED AGAINST ITS OWN LEADER.
     *
     * A single denominator across tiers is wrong in both directions, and the
     * second direction was worse. Matrix Head Start declared "501(c)(3)
     * Nonprofit" on its package policy, a keyword score of 4.5. Every
     * site-evidenced vertical was then divided by 4.5 — Early Childhood 52.5,
     * but also "mental health" at 12.0 and "dental" at 7.0, both lifted from
     * one sentence listing the services a Head Start supports a family with.
     * All three cleared 100%, hit the cap, and the report said the insured ran
     * a dental practice.
     *
     * A percentage only means something against a comparable candidate. Within
     * a tier that is exactly what its leader is.
     */
    const _vTierMax = {};
    vScored.forEach(v=>{ const t=_vTier(v); _vTierMax[t] = Math.max(_vTierMax[t]||0, v.score||0); });
    // A vertical below the best tier is capped below it. Its percentage is a
    // weight among weaker evidence, and printing 100% beside a declared answer
    // would say the two are equally settled.
    const _vCap = t => (t === _vTopTier ? 100 : (t === 1 ? 80 : 60));
    vScored.forEach(v=>{
      const t = _vTier(v);
      const base = _vTierMax[t] || vMax;
      v.confidence = t === 2 ? 100
                   : Math.min(_vCap(t), base ? Math.round((v.score / base) * 100) : 0);
    });
    const verticals = vScored.filter(v=>v.declared || (v.score >= 1.5 && v.confidence >= 50)).slice(0,4);

    // ----- States: website-grounded operations, cross-verified against the policy -----
    // Two different questions, answered from two different sources:
    //   1. WHERE DOES THE ORG OPERATE?  Only the organisation's own site can
    //      answer that — its addresses, locations and service-area language.
    //   2. WHICH STATES DOES THE POLICY NAME?  That is the policy's answer, and
    //      it is not the same question. A policy names filing states, carrier
    //      domiciles and multi-state boilerplate; a state on the paper is not
    //      proof of operations there.
    // Scoring them separately makes the disagreement visible, and the
    // disagreement is the broker-relevant part: a state the org operates in
    // that the policy never names is a potential coverage gap.
    //
    // Multi-state boilerplate clauses ("all states except ND, OH, WA, WY",
    // "Other States Insurance", WC "3.A.") enumerate states the org does NOT
    // operate in. Their CHARACTER SPANS are computed per source so any state
    // inside a span is treated as boilerplate — more reliable than a proximity
    // snippet, which bleeds across page boundaries.
    const _BP_RE = /(all\s+states?(?:\s+\w+){0,3}\s+except|other\s+states\s+insurance|states?\s+in\s+which\s+you|shown\s+in\s+(?:item|the\s+schedule)|part\s+3\.a\.|section\s+3\.a\.|3\.a\.)/gi;
    // Locative context, ANCHORED to the state name. Unanchored, this matched
    // anywhere in the preceding 40 characters, and a policy is full of words
    // that qualify: "POLICYHOLDER NOTICE - LOSS CONTROL SERVICES - CALIFORNIA"
    // earned the locative bonus from the word SERVICES, which is how a
    // California workers' comp NOTICE outscored a real address. The phrase has
    // to run INTO the state name to mean the insured is there.
    const _LOC_RE = /(?:(?:located|operat\w+|campus|headquarter\w*|based|domiciled|premises|locations?|offices?)\s+(?:in|at)\s+|principal\s+(?:place\s+of\s+business|office)\s*(?:in|at|:)?\s*|mailing\s+address\s*[:\-]?\s*|(?:proudly\s+)?serv\w+\s+(?:the\s+)?(?:greater\s+)?|,\s*)$/i;
    // The same locative test WITHOUT the bare-comma alternative. A comma is
    // what makes "Houston, TX" an address, but it is also what makes
    // "POLICY PERIOD, OR ANY" look like one. Codes that are English words
    // have to clear the higher bar.
    const _LOC_PHRASE_RE = /(?:(?:located|operat\w+|campus|headquarter\w*|based|domiciled|premises|locations?|offices?)\s+(?:in|at)\s+|principal\s+(?:place\s+of\s+business|office)\s*(?:in|at|:)?\s*|mailing\s+address\s*[:\-]?\s*|(?:proudly\s+)?serv\w+\s+(?:the\s+)?(?:greater\s+)?)$/i;
    // A comma reads as locative after a place name — "Arlington, TX" is an
    // address. It does NOT after another state: "Alabama, Alaska, Arizona" is a
    // mandate table enumerating states the organisation has nothing to do with,
    // and giving each entry the locative bonus lifted a whole notice section to
    // the same score as the state the document is actually about.
    const _SP_STATE_TOKENS = new Set(
      (typeof STATE_PROFILES !== 'undefined' ? Object.values(STATE_PROFILES) : [])
        .flatMap(sp => [String(sp.name).toLowerCase(), String(sp.abbr).toLowerCase()]));
    const _spEnumerated = before => {
      const m = /([A-Za-z][A-Za-z .]{0,24}?)\s*,\s*$/.exec(before);
      return !!m && _SP_STATE_TOKENS.has(m[1].trim().toLowerCase());
    };
    // Two things that name a state on a policy without the insured operating
    // there, both of which reach the top of the score on a long document
    // because they repeat on every page.
    //
    //   1. The CARRIER'S LETTERHEAD. "One Tower Square, Hartford, Connecticut
    //      06183" is Travelers' home office, printed on all 400 pages. The
    //      abbreviation scan already skips "TX 76006" on paper for exactly this
    //      reason; the full-name scan had no equivalent guard, so the state that
    //      won was the one the carrier is domiciled in.
    //   2. STATE AMENDATORY ENDORSEMENT TITLES. "CP T9 64 08 17 | NORTH DAKOTA
    //      CHANGES" is a form in the carrier's kit; the endorsement's presence
    //      says the form exists, not that the insured operates in North Dakota.
    //      The insured's real state is evidenced elsewhere in the document
    //      anyway, so discounting the title costs nothing where it is genuine.
    // OCR turns zeros into letters — Travelers' own footer parses as "Hartford,
    // Connecticut O6183". A strict \d{5} missed it and the carrier's home
    // office became the top-scoring operating state, so digit lookalikes count
    // as long as the group is still mostly digits.
    const _ZIP_SHAPE = /^[\s,]{0,3}(?:[A-Z]{2}[\s,]{1,3})?([0-9OoIlSs]{5})(-[0-9OoIlSs]{4})?\b/;
    const _ZIP_AFTER = after => {
      const m = _ZIP_SHAPE.exec(after);
      return !!m && (m[1].match(/\d/g) || []).length >= 3;
    };
    // A state name straight after an ISO form number is a FORM TITLE, not a
    // location: "CA 22 34 10 13 | NORTH DAKOTA PERSONAL INJURY PROTECTION
    // ENDORSEMENT". The keyword that gives it away sits at the end of the
    // title, too far to match on what follows the state name, but the form
    // number immediately before it is unmistakable.
    const _FORM_BEFORE = /\b[A-Z]{2}\s?[A-Z0-9]{1,2}\s?\d{2}(\s?\d{2}){0,2}\s*[|:\-]?\s*$/;
    // Same idea without a form number in front: an ALL-CAPS state name heading a
    // run of ALL-CAPS words that ends in a form keyword is a form title —
    // "NORTH DAKOTA UNINSURED MOTORISTS COVERAGE".
    const _TITLE_AFTER = /^[\s|:\-]{0,4}[A-Z][A-Z' \-]{2,45}\b(COVERAGE|ENDORSEMENT|CHANGES|NOTICE|AMENDMENT|PROVISIONS|DISCLOSURE|FORM|SCHEDULE)\b/;
    // The same title with the state at the END rather than the start:
    // "POLICYHOLDER NOTICE - LOSS CONTROL SERVICES - CALIFORNIA". The words
    // before it are the giveaway, and without this the title's own noun
    // ("SERVICES") used to read as locative context.
    const _TITLE_BEFORE = /\b(NOTICE|ENDORSEMENT|COVERAGE|CHANGES|AMENDMENT|PROVISIONS|DISCLOSURE|FORM|SCHEDULE|SERVICES|PROGRAM|ADDENDUM)\b[A-Z0-9' \-\u2013\u2014]{0,40}[\s\-\u2013\u2014|:]{1,5}$/;
    // "your mailing address is within California", "10 days if the place of
    // mailing is outside of California" — a conditional clause in a notice,
    // not a statement that the insured is there.
    const _CONDITIONAL_BEFORE = /\b(?:is|are|if|whether)\s+(?:with)?in\s+$|\boutside\s+of\s+$/i;
    // A state name that is part of a CITY name: "Kansas City, MO 64111" is the
    // carrier's home office in Missouri, and the comma after it read as the
    // comma of an address — which put Kansas on an Arizona school's profile.
    // Same for Oklahoma City, New York City, Jefferson City.
    const _CITY_NAME_AFTER = /^\s+City\b/i;
    // A state naming its own REGULATORY DOMAIN — "the California workers
    // compensation insurance market", "Texas unemployment tax" — describes the
    // law the form follows, not where the insured is. Lower-case, so the
    // capitalised statute pattern above never sees it, and it is the bulk of
    // what a workers' comp policy says about any state.
    const _REG_DOMAIN_AFTER = /^[\s,]{0,3}(workers'?\u2019?\s*comp\w*|insurance\s+(?:market|code|law|commissioner|department)|labor|employment|unemployment|tax(?:es|ation)?|revenue|medicaid|premium\s+rate)/i;
    // "California's Song-Beverly Credit Card Act" — a statute cited inside a
    // coverage form names a state without the insured being anywhere near it.
    const _POSSESSIVE_AFTER = /^['’]s\b/;
    // A statute named after its state — "North Dakota Auto Accident Reparations
    // Act", "Texas Insurance Code". Every policy recites the law of whichever
    // state's rules it is following on that page.
    const _STATUTE_AFTER = /^[\s,]{0,3}([A-Z][A-Za-z'\u2019\-]*\s+){0,5}(Act|Law|Code|Statutes?|Regulations?|Rules?|Plan|Bureau|Commissioner|Commission|Board|Fund|Program|Bulletin|Division|Department)\b/;
    // The one place a policy DOES evidence where the insured operates: its own
    // address block. Shape alone cannot tell it from the carrier's letterhead,
    // so the label above it decides — and when it is the insured's, the address
    // is the strongest evidence in the document rather than something to skip.
    // The carrier's NAME can contain a state — "Citizens Insurance Company of
    // Illinois", "BlueCross BlueShield of Texas". Same family as the letterhead:
    // it says who wrote the policy, not where the insured operates.
    // A state agency or benefit programme named after the state: "the North
    // Dakota Guaranty Fund", "UTAH - Medicaid and CHIP". The federal CHIP notice
    // lists every state in the union, and every policy names the guaranty fund
    // of whichever state's insolvency law it recites.
    const _AGENCY_AFTER = /^[\s|:\-\u2013\u2014]{0,6}(guarant(?:y|ee)|medicaid|chip\b|children'?s\s+health|department\s+of\s+insurance|insurance\s+department|division\s+of\s+insurance|state\s+insurance\s+fund|secretary\s+of\s+state)/i;
    // "...of <State>" after an ORGANISATION word names the body, not a place the
    // insured operates: "Workers' Compensation Insurance Rating Bureau of
    // California", "Citizens Insurance Company of Illinois".
    const _CARRIER_OF_BEFORE = /(insurance|indemnity|casualty|mutual|assurance|underwriters?|compan(?:y|ies)|group|blue\s*cross|blue\s*shield|society|association|bureau|commissioner|commission|board|fund|department|division|office|state)\s+(compan(?:y|ies)\s+)?of\s*(?:the\s+)?$/i;
    // "medicaid.utah.gov", "upp@utah.gov" — the state is part of an address for
    // a website, not for the insured.
    const _spInToken = (hay, at, len) => {
      let a=at, b=at+len;
      while(a>0 && !/\s/.test(hay[a-1])) a--;
      while(b<hay.length && !/\s/.test(hay[b])) b++;
      return /https?:\/\/|www\.|@|\.(gov|com|org|net|edu)\b/i.test(hay.slice(a,b));
    };
    // "This Additional Coverage does not apply in the State of New York" names a
    // state precisely because the insured is NOT covered there.
    const _EXCLUDING_BEFORE = /(does\s+not\s+apply\s+(?:in|to)|is\s+not\s+available\s+in|not\s+covered\s+in|excluded\s+in|except\s+in|other\s+than\s+in)\s+(?:the\s+state\s+of\s+)?$/i;
    // The carrier's attestation block: "signed by its President and Secretary at
    // Jacksonville, Florida, and countersigned on the declarations page".
    const _SIGNATURE_BEFORE = /(president|secretary|countersigned?|attest\w*|signed)\b[^.]{0,45}$/i;
    const _INSURED_CTX = /(named\s+insured|mailing\s+address|insured'?s?\s+address|location\s+of\s+premises|premises\s+address|business\s+address|insured\s*:|schedule\s+of\s+(?:premises|locations?)|premises\s+schedule|covered\s+locations?|location\s+schedule)/i;
      // An UNATTRIBUTED P.O. box. Carriers print claims and payment addresses
      // on every policy — "| Mailing Address: P.O. Box 420029 Houston, Texas
      // 77242 |" — and that made Texas an operating state on a document whose
      // insured had no Texas premises named anywhere.
      //
      // Attribution is the whole distinction, and it is why this is not simply
      // "a P.O. box is never premises". If the NAMED INSURED's own address is
      // a box number, that is still where they are addressed and often the
      // only address in the document; suppressing it would lose the insured's
      // own state to win an argument about mail drops. So the box is ignored
      // only when nothing nearby ties it to the insured.
      const _PO_BOX_BEFORE = /p\.?\s*o\.?\s*box\s+[\w-]+[^|.\n]{0,45}$/i;
      const _INSURED_NAMED_NEAR = /named\s+insured|insured'?s?\s+(?:address|mailing)/i;
    // The PRODUCER'S licensing disclosure. Every wholesale broker prints one:
    // "In California dba: FEI Insurance Services / License # 0G89298",
    // "Risk Transfer Partners does business in California as RTP Insurance
    // Services, LLC. License No. 0J029...". It says where the BROKER is
    // licensed — same family as the carrier's letterhead, and on a
    // surplus-lines placement it repeats on every page of the binder.
    const _LICENSE_NEAR = /(licen[sc]e\s*(?:no\.?|#|number)|dba\b|doing\s+business\s+as|does\s+business\s+in|surplus\s+lines?\s+broker|resident\s+agent)/i;
    // A state named as an EXAMPLE is not a place the insured operates:
    // "Certain states do not treat HSA contributions as tax-free (e.g.,
    // California and New Jersey)" is a tax footnote in a benefits guide, and
    // it put California on the profile of an Arizona charter school.
    // Marker plus "still in the same clause": the abbreviations themselves end
    // in a full stop ("e.g."), so a regex anchored on sentence punctuation
    // cannot express this.
    const _EXAMPLE_MARKER = /(e\.\s?g\.|i\.\s?e\.|for example|such as|certain states?|some states?|other states?|states? (?:do|does|may|might|will) not|residents? of|outside of)/i;
    const _exampleAside = before => {
      const m = _EXAMPLE_MARKER.exec(String(before || ''));
      if (!m) return false;
      const tail = before.slice(m.index + m[0].length);
      return !/[.;:]\s/.test(tail) && tail.length <= 60;    // no sentence ended in between
    };
    const _AMENDATORY_AFTER = /^[\s,|:\-]{0,6}(changes?|amendator|amendment|endorsement|notice|special\s+provisions|mandator)/i;
    // The same thing said as a sentence rather than a title: "If you have
    // operations conducted in, North Dakota, this endorsement modifies the
    // insurance provided..." is a form's applicability clause. It says what the
    // endorsement would do IF the insured operated there — it is not evidence
    // that they do, and it repeats on every state form in the carrier's kit.
    const _AMENDATORY_NEAR = /(this\s+endorsement|endorsement\s+modifies|changes\s+the\s+policy|amendatory\s+endorsement|this\s+notice\s+does\s+not|policyholder\s+notice)/i;
    const _snip = (hay, at, len) => { const s=Math.max(0,at-45), e=Math.min(hay.length, at+len+45); return (s>0?'…':'')+hay.slice(s,e).replace(/\s+/g,' ').trim()+(e<hay.length?'…':''); };
    // A run of "City, ST" pairs is a TABLE of cities — a catastrophe zone list,
    // a branch directory, a terrorism-rating grid. Individually each pair looks
    // exactly like an address, which is why "| Charlotte, NC | Indianapolis, IN
    // | New York, NY |" produced operating states. Density is what separates a
    // table from a real address, so any region carrying several pairs close
    // together is treated the same way as carrier boilerplate.
    // Density alone is not enough to call it a table: a SCHEDULE OF PREMISES is
    // also a dense run of "City, ST" pairs, and that one is the best evidence in
    // the document. What separates them is how many DIFFERENT states appear — an
    // insured's locations cluster in a state or three, while a branch directory
    // or catastrophe grid sweeps the country. Requiring four distinct states in
    // the run keeps multi-location schedules intact.
    const _CITY_ST_RE = /[A-Za-z][A-Za-z.\-' ]{2,24},\s*([A-Z]{2})\b/g;
    const _cityGridSpans = (text, distinctStates=4, window=420) => {
      const hits=[]; const re=new RegExp(_CITY_ST_RE.source,'g'); let m;
      while((m=re.exec(text))!==null) hits.push({ at:m.index, st:m[1] });
      const spans=[]; let i=0;
      while(i<hits.length){
        let j=i;
        while(j+1<hits.length && hits[j+1].at-hits[i].at<=window) j++;
        const states=new Set(hits.slice(i,j+1).map(h=>h.st));
        if (states.size>=distinctStates){ spans.push([Math.max(0,hits[i].at-60), hits[j].at+120]); i=j+1; }
        else i++;
      }
      return spans;
    };
    // Any region naming FOUR OR MORE DIFFERENT states close together is a list,
    // whatever its formatting — the federal CHIP notice ("COLORADO - Health
    // First Colorado", "UTAH - Premium Partnership"), a state-mandate table, a
    // guaranty-fund schedule. Matching the list rather than each entry's wording
    // is what makes this hold: the notice formats vary constantly, the density
    // does not. Four is the same bar the city grid uses, and for the same
    // reason — an insured's own locations do not sweep the country.
    const _ALL_STATE_RE = (typeof STATE_PROFILES !== 'undefined')
      ? new RegExp('\\b(' + [...new Set(Object.values(STATE_PROFILES).map(sp=>sp.name))]
          .sort((a,b)=>b.length-a.length).join('|') + ')\\b', 'gi')
      : null;
    const _stateListSpans = (text, distinct=5, window=1800) => {
      if (!_ALL_STATE_RE) return [];
      const hits=[]; const re=new RegExp(_ALL_STATE_RE.source, 'gi'); let m;
      while((m=re.exec(text))!==null) hits.push({ at:m.index, name:m[1].toLowerCase() });
      const spans=[]; let i=0;
      while(i<hits.length){
        let j=i;
        while(j+1<hits.length && hits[j+1].at-hits[i].at<=window) j++;
        if (new Set(hits.slice(i,j+1).map(h=>h.name)).size>=distinct){
          spans.push([Math.max(0,hits[i].at-60), hits[j].at+140]); i=j+1;
        } else i++;
      }
      return spans;
    };
    /* A carrier's own address is not the insured's location.
     *
     * Aspen View Academies is one campus in Castle Rock, Colorado. It was
     * reported as operating in Colorado AND Iowa, on "1111 Ashworth Road West
     * Des Moines, Iowa 50265 1-877-448-4331 www.GuideOne.com" — the insurer's
     * claims address, printed on the policy as it is on every policy. Iowa then
     * pulled a derecho catastrophe profile, an Iowa FAIR Plan assessment, Iowa
     * DOI regulatory sources and an Iowa row in the priority gap table.
     *
     * The scorer already skips a bare postal address on policy paper for this
     * reason, but an address next to a toll-free number and a carrier domain
     * still scored as a location. Marking the region suppresses it the same way
     * boilerplate is suppressed.
     *
     * Deliberately narrow. A premises or location schedule is how a genuine
     * second operating state appears on a policy, and it carries none of these
     * markers, so "Schedule of Premises: 100 Main Street, Tulsa, Oklahoma"
     * still counts. */
    const _CARRIER_ADDR_RE = new RegExp(
      '(?:www\\.[A-Za-z0-9-]+\\.com|1[-\\s]?8(?:00|33|44|55|66|77|88)[-\\s]?\\d{3}[-\\s]?\\d{4}'
      + '|insurance\\s+company|mutual\\s+insurance|underwriters|home\\s+office'
      + '|administrative\\s+office|claims?\\s+(?:address|office|center|centre)'
      + '|remit\\s+(?:to|payment)|policy\\s+issued\\s+by)', 'gi');
    const _carrierAddrSpans = text => {
      const out = []; let m;
      const re = new RegExp(_CARRIER_ADDR_RE.source, 'gi');
      while ((m = re.exec(text || '')) !== null) out.push([Math.max(0, m.index - 160), m.index + 160]);
      return out;
    };

    const _mkSpans = list => list.map(src=>{ const spans=[]; const re=new RegExp(_BP_RE.source,'gi'); let m; while((m=re.exec(src.text||''))!==null){ spans.push([m.index, m.index+260]); } return { label:src.label, weight:src.weight||1, text:src.text||'',
               spans: spans.concat(_cityGridSpans(src.text||''), _stateListSpans(src.text||''),
                                   (src.kind === 'site' ? [] : _carrierAddrSpans(src.text||''))) }; });
    const _inBP = (spans, at) => spans.some(sp=>at>=sp[0] && at<=sp[1]);

    const siteSrcs   = _mkSpans(sources.filter(x=>x.kind==='site' && x.independent!==false));
    const policySrcs = _mkSpans(sources.filter(x=>x.kind==='declarations' || x.kind==='policy'));
    const haveSite   = siteSrcs.some(x=>String(x.text||'').replace(/\s+/g,' ').trim().length>=40);

    // USPS codes that are also ordinary English words. These carry no weight
    // from punctuation alone — see the guard in the abbreviation branch.
    const _ABBR_IS_WORD = new Set(['OR','IN','ME','OK','HI','LA','PA','MA','MO',
                                   'AL','AS','ID','OH','CO','MS','MT','DE']);

    // Score one state against one group of sources.
    // isSite flips how a postal address is read: "Arlington, TX 76006" on the
    // organisation's own site IS its location, while the same shape in a policy
    // is usually the carrier's or agent's address, so the policy scan keeps
    // skipping it.
    function _scoreState(sp, srcs, isSite){
      // `locScore` counts only OPERATIONS evidence — an address, or the state
      // named in locative context. A bare mention still adds to `score`, but a
      // state that never appears as a location has none, and the caller
      // requires one before reporting the state as somewhere the insured
      // operates. Without that, a single incidental mention (weight 1.0 on the
      // first two pages, 1.2) cleared the 0.9 bar on its own.
      let score=0, locScore=0, mentions=0, best=null, bestW=-1;
      // Every counted line, not only the best: a jurisdiction named two ways
      // ("District of Columbia", "Washington, D.C.") keeps both source lines.
      const lines=[];
      const _line=(src, at, len)=>{ if(lines.length<12){ const ev=_snip(src.text,at,len); if(!lines.some(l=>l.evidence===ev)) lines.push({source:src.label, evidence:ev}); } };
      srcs.forEach(src=>{
        const hay=src.text, low=hay.toLowerCase();
        let f=0, at;
        while((at=low.indexOf(sp.name.toLowerCase(), f))>=0){
          const end=at+sp.name.length;
          const after=hay.slice(end, end+60);
          f=end;
          // "Washington, DC 20001" is DC's city, not Washington State.
          if (sp.abbr === 'WA' && typeof window.spIsDCCityAfter === 'function' && window.spIsDCCityAfter(after)) continue;
          const zip=_ZIP_AFTER(after);
          const insuredAddr=zip && _INSURED_CTX.test(hay.slice(Math.max(0,at-120), at));
          if (!isSite && zip && !insuredAddr) continue;         // carrier/agent letterhead
          const bp=_inBP(src.spans, at);
          const before0=hay.slice(Math.max(0,at-60), at);
          const amend=!isSite && (_AMENDATORY_AFTER.test(after)      // "TEXAS CHANGES", "OHIO AMENDATORY"
                                  || _TITLE_AFTER.test(after)        // "NORTH DAKOTA ... COVERAGE"
                                  || _POSSESSIVE_AFTER.test(after)   // "California's ... Act"
                                  || _STATUTE_AFTER.test(after)      // "... Reparations Act"
                                  || _AMENDATORY_NEAR.test(hay.slice(end, end+140))
                                  || _FORM_BEFORE.test(hay.slice(Math.max(0,at-30), at))
                                  || _AGENCY_AFTER.test(after)        // "... Guaranty Fund", "- Medicaid"
                                  || _spInToken(hay, at, sp.name.length)               // medicaid.utah.gov
                                  || _EXCLUDING_BEFORE.test(before0)                   // "does not apply in"
                                  || _SIGNATURE_BEFORE.test(before0)                   // carrier attestation
                                  || _CARRIER_OF_BEFORE.test(hay.slice(Math.max(0,at-45), at))
                                  || _CITY_NAME_AFTER.test(after)
                                  || _REG_DOMAIN_AFTER.test(after)
                                  || _TITLE_BEFORE.test(hay.slice(Math.max(0,at-60), at))
                                  || _CONDITIONAL_BEFORE.test(before0)
                                  || _LICENSE_NEAR.test(hay.slice(Math.max(0,at-70), end+90))
                                    || (_PO_BOX_BEFORE.test(before0)              // a mail drop…
                                        && !_INSURED_NAMED_NEAR.test(hay.slice(Math.max(0,at-170), at))));  // …that is not the insured's own
          const before=hay.slice(Math.max(0,at-40), at);
          const example=_exampleAside(hay.slice(Math.max(0,at-90), at));
          const loc=insuredAddr ? 2.0
                    : ((!_spEnumerated(before) && !example && _LOC_RE.test(before)) ? 1.5 : 1);
          const discounted = bp || amend || example;
          const w=src.weight*(discounted?0.1:loc);
          score+=w;
          if(!discounted){ mentions++; _line(src, at, sp.name.length); if(loc>1) locScore+=w; }
          if(w>bestW){ bestW=w; best={label:src.label, matched:sp.name, evidence:_snip(hay,at,sp.name.length)}; }
        }
        // PLACE NAMES. A city is better evidence of a state than the state's
        // own abbreviation, and it is the evidence most real websites actually
        // carry: matrixheadstart.org's About page says "Detroit" five times
        // and never says "MI" or "Michigan" once. "Detroit" is not an English
        // word, not an ISO form prefix and not a conjunction, so none of the
        // guards below it needs apply. See gazetteer.js for what is in the
        // list and, more importantly, what is kept out of it.
        //
        // Site only. On policy paper a city is as likely to be the carrier's
        // head office or a venue clause as the insured's premises, and states
        // do not come from paper any more in the first place.
        if (isSite && typeof window.spGazetteerCities === 'function'){
          const hits=window.spGazetteerCities(hay).filter(hit=>
            hit.abbr === sp.abbr
            && !_inBP(src.spans, hit.at)                           // boilerplate
            && !_exampleAside(hay.slice(Math.max(0,hit.at-90), hit.at)));
          // ONE city mention is a place, not a footprint. "Our founder was
          // born in Boise" names Idaho as plainly as any address does and
          // evidences nothing about where the organisation operates. A site
          // that actually works somewhere returns to the name: Matrix says
          // "Detroit" four times on its About page alone.
          //
          // So a single hit still counts as a mention — it is real, and three
          // mentions of anything clears the floor on their own — but it does
          // not count as LOCATION evidence, which is the gate that lets a
          // state be reported at all.
          const locative = hits.length >= 2;
          hits.forEach(hit=>{
            const w=src.weight*1.5;
            score+=w; mentions++;
            if (locative) locScore+=w;
            if(w>bestW){ bestW=w; best={label:src.label, matched:hit.city,
                                        evidence:_snip(hay,hit.at,hit.city.length)}; }
          });
        }

        // DC is also written "D.C." — "Washington, D.C. 20001".
        const re = sp.abbr === 'DC'
          ? /(^|[^A-Za-z])(DC|D\.\s?C\b\.?)(?![A-Za-z])/g
          : new RegExp('(^|[^A-Za-z])'+sp.abbr+'([^A-Za-z]|$)','g');
        let m, n=0;
        while((m=re.exec(hay))!==null && n<20){
          const at2=m.index+m[1].length;
          const tokLen = sp.abbr === 'DC' ? m[2].length : sp.abbr.length;
          const after=hay.slice(at2+tokLen, at2+tokLen+10);
          const isForm=/^\s+\d{2}\s+\d{2}/.test(after);       // ISO form number "IL 83 00 TX"
          const isAddr=/^\s*\d{5}\b/.test(after);             // "TX 76006"
          if (isForm){ n++; continue; }
          if (isAddr && !isSite){ n++; continue; }             // carrier/agent address on paper
          // A two-letter abbreviation is the weakest possible evidence, and on
          // a policy it is mostly not a state at all: "OF AGENT OR BROKER" is
          // Oregon, "SEE IL" is the ISO interline form prefix, and
          // "(CA, GA, IL, IO, ME, MO, NY, NC, OR, WA)" is a terrorism-exclusion
          // list of states the insured is NOT in. On paper the state that
          // matters is spelled out somewhere, so the abbreviation only counts
          // when it sits in locative context — after a city comma, or after
          // "located in" / "State:" — and never inside an enumeration.
          const before2=hay.slice(Math.max(0,at2-40), at2);
          const cityComma=/,\s*$/.test(before2) && !_spEnumerated(before2);
          if (_exampleAside(hay.slice(Math.max(0,at2-90), at2))){ n++; continue; }
          // A CODE THAT IS ALSO AN ENGLISH WORD NEEDS MORE THAN A COMMA.
          //
          // Matrix Human Services was reported as operating in Oregon. The
          // evidence: "CLAIMS FIRST MADE DURING THE POLICY PERIOD, OR ANY
          // APPLICABLE EXTENDED REPORTING PERIOD". A comma before the token is
          // what makes "Houston, TX" a location, and it makes "PERIOD, OR" one
          // too. Several USPS codes are ordinary words — OR, IN, ME, OK, HI,
          // LA, PA, MA, MO, AL, AS, ID, OH, CO, MS, MT, DE — and for those a
          // comma proves nothing. They need a ZIP after them, or an explicit
          // locative phrase before them.
          //
          // The site side skipped this guard entirely, so a bare "OR" anywhere
          // on a website counted. It applies to both sources now: a two-letter
          // token is the weakest evidence in any document.
          if (_ABBR_IS_WORD.has(sp.abbr) && !isAddr && !_LOC_PHRASE_RE.test(before2)){ n++; continue; }
          if (!isSite && !cityComma && !_LOC_RE.test(before2)){ n++; continue; }
          const bp=_inBP(src.spans, at2);
          const amend=!isSite && (_AMENDATORY_AFTER.test(after)
                                  || _AGENCY_AFTER.test(after)
                                  || _AMENDATORY_NEAR.test(hay.slice(at2+tokLen, at2+tokLen+140)));
          const mult = isSite ? (isAddr ? 2.0 : 0.4) : 0.5;    // an address on the org's own site is its location
          const w=src.weight*mult*((bp||amend)?0.1:1);
          score+=w;
          // "City, ST" and "located in ST" ARE locative; a bare abbreviation
          // elsewhere on a site is not.
          // An abbreviation counts as OPERATIONS evidence only in a postal
          // address — "Houston, TX 77242". A bare "CA" is the weakest token in
          // the document and appears inside form numbers on every page.
          if(!bp && !amend){ mentions++; _line(src, at2, tokLen); if(isAddr || (isSite && (cityComma || _LOC_RE.test(before2)))) locScore+=w; }
          if(w>bestW){ bestW=w; best={label:src.label, matched:sp.abbr, evidence:_snip(hay,at2,tokLen)}; }
          n++;
        }
      });
      return { score, locScore, mentions, best, lines };
    }

    const byAbbr=new Map();
    const _policyOnly=new Map();
    if (typeof STATE_PROFILES !== 'undefined'){
      const seen=new Set();
      Object.values(STATE_PROFILES).forEach(sp=>{
        if (seen.has(sp.abbr)) return; seen.add(sp.abbr);
        const site   = haveSite ? _scoreState(sp, siteSrcs, true) : { score:0, best:null };
        const policy = _scoreState(sp, policySrcs, false);
        // Website evidence must be OPERATIONS evidence, so the bar is higher
        // than a passing mention: an address, or a name in locative context.
        // A state is reported only where the source EVIDENCES OPERATIONS: an
        // address or locative phrasing (locScore), or a source that returns to
        // the state repeatedly outside boilerplate (three mentions or more).
        // One passing mention used to be enough on its own — and the count has
        // to be of MENTIONS, not weighted score, or a single line on a
        // high-weight website source clears the bar by weight alone.
        const onSite   = site.score   >= 1.2 && (site.locScore > 0 || site.mentions >= 3);
        const onPolicy = policy.score >= 0.9 && (policy.locScore > 0 || policy.mentions >= 3);
        // OPERATING STATES COME FROM THE SITE ONLY.
        //
        // Where an organisation operates is a fact about the organisation, and
        // the organisation's own description of itself is the evidence for it.
        // A policy NAMES states for many reasons that are not operations: the
        // carrier's own address, a state amendatory endorsement, a schedule of
        // mandates, a notice printed in every copy of the form. Those produced
        // operating states the insured does not operate in.
        //
        // The policy side is still scored and still shown, but only as
        // corroboration of a state the site already evidences. It can no
        // longer introduce one.
        if (!onSite) {
          // Recorded before the return, or it is lost: the early exit is why
          // the first cut of this change returned an empty policyOnlyStates.
          if (onPolicy) _policyOnly.set(sp.abbr, {
            abbr: sp.abbr, name: sp.name,
            evidence: policy.best ? policy.best.evidence : '',
            source: policy.best ? policy.best.label : ''
          });
          return;
        }
        const b = site.best || policy.best;
        byAbbr.set(sp.abbr, {
          abbr: sp.abbr, name: sp.name,
          score: Math.max(site.score, policy.score),
          siteScore: site.score, policyScore: policy.score,
          onSite: onSite, onPolicy: onPolicy,
          siteLoc: site.locScore, policyLoc: policy.locScore,
          siteMentions: site.mentions, policyMentions: policy.mentions,
          evidence: b ? b.evidence : '', source: b ? b.label : '', matched: b ? b.matched : sp.abbr,
          siteEvidence: site.best ? site.best.evidence : '',
          evidenceAll: (site.lines || []).concat(policy.lines || []),
          policyEvidence: policy.best ? policy.best.evidence : '',
          // 'confirmed'      — operates there AND the policy names it
          // 'not-on-policy'  — operates there, policy never names it: possible gap
          // 'policy-only'    — policy names it, the site shows no operations there
          // 'no-site'        — no independent site text to check against
          xcheck: !haveSite ? 'no-site' : (onSite && onPolicy ? 'confirmed' : (onSite ? 'not-on-policy' : 'policy-only'))
        });
      });
    }
    // WHERE THE SOURCES SHOW LOCATIONS, ONLY LOCATIONS COUNT.
    //
    // Repetition is weak evidence, and on a policy it is often not evidence at
    // all: the Base Power workers' comp policy names California 78 times — in
    // WCIRB notices, the Labor Code, market-conduct text — while the only
    // address in the whole set is in Houston. But repetition is ALL a benefits
    // guide gives: it never prints an address, and its state is simply the one
    // it keeps naming.
    //
    // So the two are not ranked against each other, they are tiered. If any
    // candidate carries real location evidence — an address block, a premises
    // schedule, locative phrasing running into the name — then every candidate
    // without it is dropped. Only when NO source located anyone does the
    // mention count decide.
    const _hasLoc = x => (x.siteLoc > 0 || x.policyLoc > 0);
    const _located = [...byAbbr.values()].filter(_hasLoc);
    const _pool = _located.length ? _located : [...byAbbr.values()];

    const _xrank = { 'confirmed':0, 'not-on-policy':1, 'no-site':2, 'policy-only':3 };
    const sScored = _pool.sort(
      (a,b)=> (_xrank[a.xcheck]-_xrank[b.xcheck]) || (b.score-a.score));
    // Same normalisation as the verticals: score relative to the best-evidenced
    // state, so the floor holds whatever the document's length or vocabulary.
    const sMax = sScored.reduce((m,x)=>Math.max(m, x.score), 0);
    sScored.forEach(x=>{ x.confidence = sMax ? Math.round((x.score / sMax) * 100) : 0; });

    // Every state here is site-evidenced by construction (see above). The
    // floor still applies, because a site can mention a state in passing.
    let states = sScored.filter(x=>x.confidence >= 25);

    // Never return nothing WHEN THERE IS SITE EVIDENCE TO RETURN. With no
    // website read there is nothing to fall back to, and reporting a state
    // taken from the paper would be the very thing this rule removes.
    if (!states.length && sScored.length) states = sScored.slice(0, 1);

    // States the POLICY names that the site does not evidence. No longer
    // reported as operating states; kept so the information is not destroyed,
    // and so a caller can show the inverse question — paper naming a state the
    // organisation does not appear to operate in.
    const policyOnlyStates = [..._policyOnly.values()];

    // One record per jurisdiction, before anything reads the list.
    if (typeof window.spOneRecordPerJurisdiction === 'function') states = window.spOneRecordPerJurisdiction(states);
    states = states.slice(0, 4);

    return { verticals, states, policyOnlyStates };
  };

  // Render the auto-detected, evidence-grounded profile into the report.
  window.spRenderProfileCard = function(prof){
    if (!prof) return;
    const esc = s => { const d=document.createElement('div'); d.textContent=String(s==null?'':s); return d.innerHTML; };
    const row = (title, items, colorBadge, emptyWhy) => {
      let h='<div style="margin:8px 0;"><div style="font-weight:600;margin-bottom:4px;">'+title+' <span style="font-weight:400;color:#666;">('+items.length+' detected)</span></div>';
      if (!items.length){
        const st = window._spSourceStats || {};
        let why;
        if (!st.docsUsed && !st.siteChars){
          why = st.excluded
            ? 'No usable evidence: the only document(s) uploaded were SentriPol-generated report(s), which are excluded'
            : (st.docs ? 'No text could be read from the uploaded document(s)'
                       : 'No policy documents were parsed');
          why += st.siteError ? (', and the website fetch failed (' + esc(st.siteError) + ').')
                              : (st.siteChars ? '.' : ', and no website text was available.');
        } else if (!st.siteChars && st.siteError){
          why = 'Nothing matched in the policy documents, and the website fetch failed (' + esc(st.siteError) + ').';
        } else {
          why = 'Nothing matched in the policy documents or website text.';
        }
        // Operating states are read from the WEBSITE only now, so the generic
        // "nothing matched in the policy documents or website text" is wrong
        // for that row: the policy was never allowed to contribute. The caller
        // supplies a row-specific explanation, and it wins wherever there was
        // evidence to search at all.
        if (emptyWhy && (st.docsUsed || st.siteChars)){ const o = emptyWhy(st); if (o) why = o; }
        h += '<div style="color:#a00;font-size:12px;">'+why+'</div></div>';
        return h;
      }
      items.forEach(it=>{
        const label = it.name || it.abbr;
        // A STATUS is declared, not scored. Printing "2%" beside "501(c)(3)
        // Nonprofit" invites the reader to weigh it against the industry, which
        // is the comparison this whole change exists to stop: they answer
        // different questions and neither is a weaker version of the other.
        const conf = SP_STATUS_VERTICALS.has(it.name) ? ''
          : (typeof it.score==='number') ? ' <span style="font-size:10px;color:#999;">score '+it.score.toFixed(1)+'</span>' : '';
        // GATE 3 verdict — does the company's own site say it is in this line of business?
        const pill=(bg,bd,fg,txt,tip)=>' <span title="'+tip+'" style="font-size:10px;color:'+fg+';background:'+bg+';border:1px solid '+bd+';border-radius:8px;padding:1px 6px;">'+txt+'</span>';
        const site = !it.site ? ''
          : it.site==='confirmed' ? pill('#e8f5e9','#a5d6a7','#1b5e20','✓ site','Corroborated by the company website / overview')
          : it.site==='declared'  ? pill('#ede7f6','#b39ddb','#4527a0','✓ declared','Stated in the policy declarations — a labelled Business Description / Nature of Business field, which is the carrier\u2019s own statement of the insured\u2019s trade')
          : it.site==='absent'    ? pill('#fff8e1','#ffe082','#8a6d00','⚠ not on site','The company website / overview does not describe this line of business — this vertical comes from policy paper only')
          :                         pill('#f5f5f5','#e0e0e0','#616161','no site info','No independent website / overview text was supplied — paste the company overview to enable the cross-check');
        // States carry a two-way verdict: operations (website) vs the states the
        // policy actually names. A state the org operates in that the policy
        // never names is the broker-relevant case, so it gets the loudest pill.
        const xc = !it.xcheck ? ''
          : it.xcheck==='confirmed'     ? pill('#e8f5e9','#a5d6a7','#1b5e20','✓ site + policy','Operations evidenced on the company website AND the state is named in the uploaded policy documents')
          : it.xcheck==='not-on-policy' ? pill('#ffebee','#ef9a9a','#b71c1c','⚠ not on policy','The website evidences operations in this state but no uploaded policy document names it — verify the policy covers this location')
          : it.xcheck==='policy-only'   ? pill('#fff8e1','#ffe082','#8a6d00','policy only','Named in the policy documents, but the website shows no operations there — may be a filing or carrier domicile state rather than an operating location')
          :                               pill('#f5f5f5','#e0e0e0','#616161','no site info','No independent website text was available, so operating states could not be cross-checked against the policy');
        h+='<details style="margin:3px 0;background:#fff;border:1px solid #e3e0f5;border-radius:4px;">'
          +'<summary style="cursor:pointer;padding:5px 8px;">'+colorBadge(label)+conf+site+xc+' <span style="font-size:11px;color:#777;">— evidence in '+esc(it.source)+'</span></summary>'
          +'<div style="padding:4px 10px 8px;font-size:12px;color:#333;">matched “<strong>'+esc(it.matched)+'</strong>”: <em>'+esc(it.evidence)+'</em>'
          +(it.siteEvidence&&it.siteMatched?'<div style="margin-top:4px;color:#1b5e20;">site says “<strong>'+esc(it.siteMatched)+'</strong>”: <em>'+esc(it.siteEvidence)+'</em></div>':'')
          +(it.xcheck&&it.siteEvidence&&!it.siteMatched?'<div style="margin-top:4px;color:#1b5e20;">website: <em>'+esc(it.siteEvidence)+'</em></div>':'')
          +(it.xcheck&&it.policyEvidence?'<div style="margin-top:2px;color:#555;">policy: <em>'+esc(it.policyEvidence)+'</em></div>':'')
          +(it.xcheck==='not-on-policy'?'<div style="margin-top:4px;color:#b71c1c;">No uploaded policy document names this state. Confirm the policy covers operations here before relying on it.</div>':'')
          +(it.site==='absent'?'<div style="margin-top:4px;color:#8a6d00;">Not corroborated by the company\'s own description of itself. Confirm the line of business with the broker before relying on this vertical.</div>':'')
          +'</div></details>';
      });
      return h+'</div>';
    };
    const vBadge = t=>'<span style="display:inline-block;padding:2px 8px;border-radius:10px;background:#5b2be0;color:#fff;font-size:12px;">'+esc(t)+'</span>';
    const _vShow = window.spVerticalsForDisplay(prof.verticals);
    const sBadge = t=>'<span style="display:inline-block;padding:2px 8px;border-radius:10px;background:#00695c;color:#fff;font-size:12px;">'+esc(t)+'</span>';
    const method = prof.method ? esc(prof.method) : 'grounded';
    const html='<div id="spDetectedProfile" style="border:1px solid #5b2be0;border-radius:8px;padding:12px 14px;margin:0 0 16px;background:#faf9ff;">'
      +'<div style="font-weight:700;color:#5b2be0;margin-bottom:6px;">🔎 Auto-Detected Profile '
      +'<span style="font-weight:600;font-size:10px;color:#fff;background:#5b2be0;padding:1px 7px;border-radius:9px;vertical-align:middle;">'+method+'</span> '
      +'<span style="font-weight:400;font-size:11px;color:#777;">— top 1–4 verticals &amp; 1–2 states, mapped from policy docs + website; every item grounded in a quoted source (no user input, no hallucination)</span></div>'
      +row('Industry Verticals', _vShow.shown.filter(v=>!SP_STATUS_VERTICALS.has(v.name)), vBadge)
      // Reported, separately, because it is a different axis. A non-profit Head
      // Start's industry is child day care; its non-profit status changes D&O,
      // who the insureds are and the exposure, and belongs on the page — just
      // not in a list the reader ranks against the line of business.
      +(function(){
        const st=_vShow.shown.filter(v=>SP_STATUS_VERTICALS.has(v.name));
        if(!st.length) return '';
        return '<div style="margin:2px 0 8px;padding:6px 8px;background:#f4f1fa;border:1px solid #d6c9ee;border-radius:5px;font-size:11px;color:#3f2b63;">'
          +'Organisational form: <strong>'+st.map(v=>esc(v.name)).join(', ')+'</strong> — '
          +(st[0].site==='declared'||st[0].site==='confirmed'
              ? 'stated in the policy declarations. ' : '')
          +'This is what the organisation IS, not what it does. It bears on D&amp;O, on who the insureds are and on the exposure; the line of business is above.'
          +'</div>';
      })()
      // THE THREE ANSWERS, SAID TOGETHER.
      //
      // Matrix Head Start: declarations "Non-Profit with Social Service
      // Agencies", website early childhood education, NAICS 624410 Child Day
      // Care Services. Three panels, three answers, no statement of how they
      // relate — so a reader had to work out for themselves that none of them
      // disagrees. Where they genuinely DO disagree, that is an underwriting
      // question and it should be the loudest thing on the card, not something
      // to be noticed.
      +(function(){
        const naics=((window._naicsAnalysis||{}).naics)||{};
        const industry=_vShow.shown.filter(v=>!SP_STATUS_VERTICALS.has(v.name));
        const site=industry.find(v=>v.site==='confirmed');
        const decl=industry.find(v=>v.declared);
        const rows=[];
        if(decl)        rows.push(['The declarations say', decl.name, decl.source||'policy']);
        if(site)        rows.push(['The website evidences', site.name, site.source||'website']);
        if(naics.code)  rows.push(['NAICS '+esc(naics.code)+' maps to', naics.vertical||'—', naics.source||'classifier']);
        if(rows.length<2) return '';
        const names=rows.map(r=>String(r[1]));
        const agree=names.every(n=>n===names[0]);
        return '<div style="margin:2px 0 10px;padding:7px 9px;border-radius:5px;font-size:11px;'
          +(agree?'background:#e8f5e9;border:1px solid #a5d6a7;color:#1b5e20;'
                 :'background:#fff8e1;border:1px solid #ffe082;color:#6d4c00;')+'">'
          +'<strong>'+(agree?'The three readings agree.':'The three readings differ.')+'</strong>'
          +'<div style="margin-top:3px;">'
          +rows.map(r=>esc(r[0])+': <strong>'+esc(r[1])+'</strong> <span style="color:#777;">('+esc(r[2])+')</span>').join('<br>')
          +'</div>'
          +(agree?'' : '<div style="margin-top:4px;">These are three different questions — what the contract says the insured is, what it says about itself, and what its primary revenue activity classifies as — so they need not match. Confirm the line of business with the broker where the difference would change the cover.</div>')
          +'</div>';
      })()
      // Named, not silently dropped: "the paper suggests X and the site never
      // mentions it" is a finding a broker should see, and where NOTHING is
      // corroborated the analysis still had to classify the account, so the
      // classification it used is stated rather than left as a blank section.
      +(_vShow.hidden.length
        ? '<div style="margin:-4px 0 8px;padding:6px 8px;background:#fff8e1;border:1px solid #ffe082;border-radius:5px;font-size:11px;color:#6d4c00;">'
          +'Not shown — the company website does not describe: <strong>'+_vShow.hidden.map(v=>esc(v.name)).join(', ')+'</strong>. '
          +'These come from policy paper only, where the wording describes what the FORM covers rather than what the business does.'
          +(!_vShow.shown.length && prof.verticals && prof.verticals.length
            ? ' The analysis was classified as <strong>'+esc(prof.verticals[0].name)+'</strong>; confirm the line of business with the broker.'
            : '')
          +'</div>'
        : '')
      +row('Operating States', prof.states, sBadge, st => {
          const pOnly = (prof.policyOnlyStates||[]).map(s=>s.abbr).join(', ');
          const tail = pOnly
            ? ' Named on policy paper only: <strong>'+esc(pOnly)+'</strong> — a carrier address, a state amendatory form or a cited statute is not evidence of where the insured operates.'
            : '';
          if (!st.siteChars) return 'Operating states are read from the company website only. No website text was available'
            + (st.siteError ? ' (' + esc(st.siteError) + ')' : '') + '.' + tail;
          return 'The website text names no state in a locative context — a postal address, \u201Clocated in\u2026\u201D, or a served-area phrase. A state mentioned once in passing is below the evidence floor.' + tail;
        })
      // What the address pass did. An address that was read, transcribed
      // correctly and then SET ASIDE is a finding: it is the difference
      // between "we found no premises there" and "the only thing there was a
      // customer's home". Silence would read as the first when it was the
      // second.
      +(function(){
        const ok=(prof.verifiedAddresses||[]), no=(prof.rejectedAddresses||[]);
        if(!ok.length && !no.length) return '';
        const roles={};
        no.forEach(r=>{ const k=String(r.reason||'').replace(/^role:/,''); roles[k]=(roles[k]||0)+1; });
        const _label={ testimonial:'a customer or resident address',
                       mailing:'a P.O. box or mail drop', carrier:'a carrier address',
                       agent:'a broker or agency address', vendor:'a vendor or partner address',
                       statutory:'an address inside a notice or statute',
                       'not-in-text':'not found in the site text',
                       'no-state':'no state given' };
        const parts=Object.keys(roles).map(k=>roles[k]+' \u00d7 '
          +(_label[k] || (k.indexOf('zip-mismatch')===0 ? 'ZIP did not match the state given' : k)));
        return '<div style="margin:2px 0 8px;padding:6px 8px;background:#f3f6ff;border:1px solid #c9d6ff;border-radius:5px;font-size:11px;color:#26417a;">'
          +'Addresses read from the website: <strong>'+ok.length+'</strong> verified as the insured\u2019s own premises'
          +(no.length ? ', <strong>'+no.length+'</strong> set aside \u2014 '+esc(parts.join('; ')) : '')
          +'. Every accepted address was found verbatim in the site text and its ZIP checked against the state.'
          +'</div>';
      })()
      +((window._spExcludedSources||[]).length
        ? '<div style="margin:6px 0 0;padding:6px 8px;background:#fff8e1;border:1px solid #ffe082;border-radius:5px;font-size:11px;color:#6d4c00;">'
          +'Excluded from evidence: <strong>'+(window._spExcludedSources||[]).map(esc).join(', ')+'</strong> — SentriPol-generated report(s). '
          +'A report cannot be cited as evidence for its own findings. Upload the underlying policy documents instead.</div>'
        : '')
      +'</div>';
    // Mounts into Section 3 of the report ("Auto-Detected Profile & NAICS Vertical
    // Analysis"). Quick Screen has no numbered sections and no host, so there the
    // panel still goes to the top of the tab.
    ['fullReview','quickScreen'].forEach(id=>{
      const el=document.getElementById(id);
      if (!el || el.querySelector('#spDetectedProfile-'+id)) return;
      const block = html.replace('id="spDetectedProfile"','id="spDetectedProfile-'+id+'"');
      const host = el.querySelector('#spSection3Body');
      if (host) { host.querySelector('p') && (host.innerHTML=''); host.insertAdjacentHTML('afterbegin', block); }
      else el.insertAdjacentHTML('afterbegin', block);
    });
  };
})();
